// src/PlannerPage.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { DateTime } from "luxon";
import CreateRaidPage, {
  CreateRaidFormValues,
  CreateRaidGuild,
  CreateRaidCharacter,
} from "./CreateRaidPage";
import { HeuristicCharacter, isCharacterAvailable } from "./heuristic_roster";
import { useConfirm } from "./ConfirmDialog";
import SoftReservePage from "./SoftReservePage";
import AssignmentBoardPage from "./assignments/AssignmentBoardPage";
import CharacterRatingModal from "./CharacterRatingModal";
import { API_URL } from './config';
import ArcPage from './ArcPage';
import PlannerChatSection from './PlannerChatSection'; 
import { useToast } from './Toast';
import PlannerLootCouncilPanel from "./PlannerLootCouncilPanel";
import { useTimezone } from "./lib/TimezoneContext";
import {
  formatRaidTime,
  formatIanaTzShortLabel,
  getResetWeekForDate,
  pickerPartsToUtc,
  shiftWallClock,
  utcToPickerParts,
} from "./lib/time";
import { sessionWindowsForRaid } from "./lib/raidWindows";
import { PickerTzLabel } from "./components/PickerTzLabel";
import LootQueueArena, { ArenaPlayer } from "./components/LootQueueArena";
import { GuildWeeklyRelease } from "./GuildWeeklyRelease";
import { ThemeProvider, useTheme } from "./theme/ThemeProvider";
import { R } from "./theme/tokens";
import { PaletteContext } from "./theme/legible";
import { BODY, DISPLAY } from "./alignmentUi";

import { shortGameName } from "./lib/gameName";
import OpenSeatsPanel from "./OpenSeatsPanel";
// Client-side session id for the Loot Queue Arena (uuid; nothing persisted).
function newArenaSessionId(): string {
  try {
    const c: any = (typeof crypto !== "undefined" ? crypto : null);
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch { /* ignore */ }
  return `lqa_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL || "";

// Moved to lib/raidWindows so the mobile roster editor shares one definition
// of which bit ranges a raid covers. Re-exported name kept for readability.


// When a raid starts, for marking its week on the release switch. The switch
// resolves the week with the guild's own reset rule. Null when the raid has no
// session yet, which just means no week gets marked.
function raidFirstStart(raid: any): string | null {
  return raid?.sessions?.[0]?.starts_at ?? raid?.sessions?.[0]?.startsAt ?? null;
}

// --- TYPES ---

export type RaidDifficulty = "Normal" | "Heroic" | "Mythic" | "Ascended";
export type RaidSize = 10 | 25 | 30 | 40;

export type ScheduledRaid = {
    id: string;
    gameVersion: string;
    region: "EU" | "US";
    realm: string;
    raidName: string;
    difficulty: RaidDifficulty;
    size: RaidSize;
    
    // Date Info
    sessions?: any[];
    anchorDate: Date;
    endDate: Date;
    localTimeLabel: string;
    
    // Meta
    roleSummary: string;
    raidLeader: string;
    lootMaster: string;
    hostedByGuild: string;
    guild_id: string;
    guildLogo?: string | null;
    seriesId?: string;
    
    // My Assignment
    myCharName?: string;
    myCharClass?: string;
    myCharRole?: string;
    myCharImage?: string | null;
  
    // Raid image
    raidImageUrl?: string | null;

    // Soft Reserve status
    srEnabled?: boolean;
    srLocked?: boolean;
    srMyCount?: number;
    srMax?: number;
    srMyItems?: string[] | null;
    isPublished?: boolean;
  };

type PlannerPageProps = {
    guilds: CreateRaidGuild[];
    characters: CreateRaidCharacter[];
    accountAvailability: boolean[];
    // Admin "view as guild" mode: raid list is fetched for this guild instead
    // of the logged-in user's own guilds (site admins only, see App.tsx).
    adminViewGuildId?: string | null;
  };

  type SuggestionGroup = {
    role: string;
    needed: number;
    candidates: (HeuristicCharacter & { wclScore?: number })[];
  };
  
  // --- SQUAD TYPES ---
  interface SquadInfo {
    squadId: string;
    squadName: string;
    squadSize: number;
    memberUserIds: string[];
  }
  
  type SquadLookup = Record<string, SquadInfo>; // keyed by userId

  interface FieldOption {
    id: number;
    key: string;
    label: string;
    metadata: any;
    image_url?: string | null;
  }

  interface FieldGroup {
    id: number;
    key: string;
    label: string;
    multiSelect: boolean;
    isRequired: boolean;
    options: FieldOption[];
  }

  interface CompositionTag {
    id: number;
    tag_key: string;
    tag_label: string;
    tag_category: string;
    tag_subcategory: string | null;
    description: string | null;
    sort_order: number;
  }

  type TagCoverage = {
    tag: CompositionTag;
    covered: boolean;
    providers: string[];
  };

  type CategoryCoverage = {
    category: string;
    subcategory: string | null;
    tags: TagCoverage[];
    coveredCount: number;
    totalCount: number;
  };

// --- VISUAL UTILS ---

const CLASS_COLORS: Record<string, string> = {
  Warrior: "#C79C6E", Paladin: "#F58CBA", Hunter: "#ABD473", Rogue: "#FFF569",
  Priest: "#FFFFFF", DeathKnight: "#C41F3B", Shaman: "#0070DE", Mage: "#40C7EB",
  Warlock: "#8787ED", Monk: "#00FF96", Druid: "#FF7D0A", DemonHunter: "#A330C9", Evoker: "#33937F",
};

// --- TIME UTILS ---
function formatTime(totalMinutes: number): string {
  if (totalMinutes > 24 * 60) totalMinutes = 24 * 60;
  if (totalMinutes < 0) totalMinutes = 0;
  const renderMinutes = totalMinutes === 24 * 60 ? 0 : totalMinutes;
  const outH = Math.floor(renderMinutes / 60).toString().padStart(2, "0");
  const outM = (renderMinutes % 60).toString().padStart(2, "0");
  return `${outH}:${outM}`;
}

function adjustTime(timeStr: string, minutes: number): string {
  const [hStr, mStr] = timeStr.split(":");
  let total = (Number(hStr) || 0) * 60 + (Number(mStr) || 0) + minutes;
  const max = 24 * 60;
  total = ((total % max) + max) % max;
  return formatTime(total);
}

// Class, parse and role colors arrive as hex authored for a dark background;
// several vanish on the light palette (Priest is white). PlannerPage sets this
// from the active theme on every render, before any child renders, so every
// helper below returns a legible color. Identity on dark. See ThemeProvider.
let legible: (hex: string) => string = (hex) => hex;

function getClassColor(className: string | null | undefined): string {
  if (!className) return legible("#A3A3A3");
  const key = className.replace(/\s+/g, "");
  if (CLASS_COLORS[key]) return legible(CLASS_COLORS[key]);
  const found = Object.keys(CLASS_COLORS).find(
    (k) => k.toLowerCase() === key.toLowerCase()
  );
  return legible(found ? CLASS_COLORS[found] : "#A3A3A3");
}

function getClassIconUrl(className: string | null | undefined, fields: FieldGroup[] | null | undefined): string | null {
    if (!className || !fields) return null;
    const classGroup = fields.find(g => g.key === 'class');
    if (!classGroup) return null;
    const opt = classGroup.options.find(o => o.label === className);
    return opt?.image_url || null;
  }
  
  function getSpecIconUrl(specLabel: string | null | undefined, className: string | null | undefined, fields: FieldGroup[] | null | undefined): string | null {
    if (!specLabel || !fields) return null;
    const specGroup = fields.find(g => g.key === 'spec');
    if (!specGroup) return null;
    const classKey = className?.toLowerCase().replace(/\s+/g, '') || '';
    const opt = specGroup.options.find(o => o.label === specLabel && o.metadata?.class_key === classKey);
    return opt?.image_url || null;
  }
  
  function getRoleIcon(role: string): string {
  switch (role?.trim()) {
    case "Tank": return "\u{1F6E1}";
    case "Healer": return "\u{1F49A}";
    case "Melee DPS": return "\u2694";
    case "Ranged DPS": return "\u{1F3F9}";
    default: return "\u{1F464}";
  }
}

function getParseColor(score: number | undefined): string {
  return legible(rawParseColor(score));
}

function rawParseColor(score: number | undefined): string {
  if (score === undefined) return "#555";
  if (score === 100) return "#e5cc80";
  if (score >= 99) return "#e268a8";
  if (score >= 95) return "#ff8000";
  if (score >= 75) return "#a335ee";
  if (score >= 50) return "#0070dd";
  if (score >= 25) return "#1eff00";
  return "#9d9d9d";
}

const ROLE_LABEL_MAP: Record<string, string> = { 'tank': 'Tank', 'healer': 'Healer', 'melee_dps': 'Melee DPS', 'ranged_dps': 'Ranged DPS' };

function resolveRoleLabels(c: any, fields: FieldGroup[] | null | undefined): string[] {
  const primaryRole = (c.primaryRole ?? c.primary_role ?? c.metadata?.primary_role ?? "") as string;
  const trimmedPrimary = primaryRole ? primaryRole.trim() : "";
  if (!fields || !Array.isArray(c.options)) {
    // Fallback to metadata role_labels
    const metaLabels = c.metadata?.role_labels || c.roles;
    if (Array.isArray(metaLabels) && metaLabels.length > 0) return metaLabels;
    return trimmedPrimary ? [trimmedPrimary] : [];
  }
  const roleGroup = fields.find((g) => g.key === "role");
  if (!roleGroup) {
    const metaLabels = c.metadata?.role_labels || c.roles;
    if (Array.isArray(metaLabels) && metaLabels.length > 0) return metaLabels;
    return trimmedPrimary ? [trimmedPrimary] : [];
  }
  const rawOptions: any[] = c.options ?? [];
  let labels = rawOptions
    .filter((o: any) => o && Number(o.group_id) === Number(roleGroup.id))
    .map((o: any) => {
      const opt = roleGroup.options.find((fo: any) => Number(fo.id) === Number(o.option_id));
      return (opt?.label ?? "").trim();
    })
    .filter((lbl: string) => lbl.length > 0);
  if (!labels.length) {
    const metaLabels = c.metadata?.role_labels || c.roles;
    if (Array.isArray(metaLabels) && metaLabels.length > 0) return metaLabels;
    return trimmedPrimary ? [trimmedPrimary] : [];
  }
  return labels;
}

function resolveSpecEntries(c: any, fields: FieldGroup[] | null | undefined): { specLabel: string; roleLabel: string; specKey: string }[] {
  if (!fields || !Array.isArray(c.options)) {
    const meta = c.metadata || {};
    const specLabels: string[] = meta.spec_labels || [];
    // spec_role_labels is aligned 1:1 with spec_labels (see buildCharacterMetadata).
    // role_labels is the DEDUPED "roles this char can fill" set — only safe to index
    // by spec position when roles happen to be distinct, so it's a fallback only.
    // The roleLabels[0]/primary_role_label tail keeps un-resaved chars from showing
    // a bogus grey "DPS" on their 2nd+ spec (e.g. a Disc/Holy priest's Holy card).
    const specRoleLabels: string[] = meta.spec_role_labels || [];
    const roleLabels: string[] = meta.role_labels || [];
    if (specLabels.length > 0) {
      return specLabels.map((sl: string, i: number) => ({
        specLabel: sl,
        roleLabel: specRoleLabels[i] || roleLabels[i] || roleLabels[0] || meta.primary_role_label || "DPS",
        specKey: (meta.spec_keys || [])[i] || sl.toLowerCase(),
      }));
    }
    const roles = resolveRoleLabels(c, fields);
    return roles.map((r: string) => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, '_') }));
  }
  const specGroup = fields.find((g) => g.key === "spec");
  if (!specGroup) {
    const roles = resolveRoleLabels(c, fields);
    return roles.map((r: string) => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, '_') }));
  }
  const rawOptions: any[] = c.options ?? [];
  const entries = rawOptions
    .filter((o: any) => o && Number(o.group_id) === Number(specGroup.id))
    .map((o: any) => {
      const opt = specGroup.options.find((fo: any) => Number(fo.id) === Number(o.option_id));
      if (!opt) return null;
      const specLabel = (opt.label ?? "").trim();
      const roleKey = (opt.metadata?.role_key ?? "").trim();
      const roleLabel = ROLE_LABEL_MAP[roleKey] || roleKey || "DPS";
      const specKey = (opt.key ?? specLabel.toLowerCase().replace(/\s+/g, '_'));
      return specLabel ? { specLabel, roleLabel, specKey } : null;
    })
    .filter((e: any): e is { specLabel: string; roleLabel: string; specKey: string } => e !== null);
  if (entries.length > 0) return entries;
  const roles = resolveRoleLabels(c, fields);
  return roles.map((r: string) => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, '_') }));
}

function getCharacterProvidedTags(c: any, fields: FieldGroup[] | null | undefined, assignedRole: string | null): string[] {
  if (!fields) return [];
  const specGroup = fields.find((g) => g.key === "spec");
  if (!specGroup) return [];
  const rawOptions: any[] = c.options ?? [];
  const specEntries = rawOptions.filter((o: any) => o && Number(o.group_id) === Number(specGroup.id));
  const allTags = new Set<string>();
  for (const entry of specEntries) {
    const opt = specGroup.options.find((fo: any) => Number(fo.id) === Number(entry.option_id));
    if (!opt) continue;
    const tags: string[] = opt.metadata?.provides_tags || [];
    if (assignedRole && specEntries.length > 1) {
      const roleKey = (opt.metadata?.role_key ?? "").trim();
      if (ROLE_LABEL_MAP[roleKey] === assignedRole) {
        tags.forEach((t: string) => allTags.add(t));
      }
    } else {
      tags.forEach((t: string) => allTags.add(t));
    }
  }
  return Array.from(allTags);
}

function computeBuffCoverage(
  slotCharIds: (string | null)[],
  slotRoles: (string | null)[],
  charLookup: Map<string, any>,
  fields: FieldGroup[] | null | undefined,
  compositionTags: CompositionTag[]
): CategoryCoverage[] {
  if (!compositionTags.length || !fields) return [];
  const tagProviders = new Map<string, string[]>();
  slotCharIds.forEach((charId, idx) => {
    if (!charId) return;
    const char = charLookup.get(charId);
    if (!char) return;
    const role = slotRoles[idx];
    const tags = getCharacterProvidedTags(char, fields, role);
    for (const t of tags) {
      if (!tagProviders.has(t)) tagProviders.set(t, []);
      tagProviders.get(t)!.push(char.name || char.character_name || "Unknown");
    }
  });
  const groupMap = new Map<string, { category: string; subcategory: string | null; tags: TagCoverage[] }>();
  for (const tag of compositionTags) {
    const key = `${tag.tag_category}|||${tag.tag_subcategory || ''}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, { category: tag.tag_category, subcategory: tag.tag_subcategory, tags: [] });
    }
    const providers = tagProviders.get(tag.tag_key) || [];
    groupMap.get(key)!.tags.push({ tag, covered: providers.length > 0, providers });
  }
  return Array.from(groupMap.values()).map(g => ({
    ...g,
    coveredCount: g.tags.filter(t => t.covered).length,
    totalCount: g.tags.length,
  }));
}

function softBgFromHex(color: string, alpha = 0.18): string | undefined {
  const m = color.trim().match(/^#?([0-9a-fA-F]{6})$/);
  if (!m) return undefined;
  const hex = m[1];
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getRoleColorForRoleLabel(c: any, fields: FieldGroup[] | null | undefined, roleLabel: string): string | null {
  if (!fields || !roleLabel) return null;
  const roleGroup = fields.find((g) => g.key === "role");
  if (!roleGroup || !Array.isArray(c.options)) return null;
  const rawOptions: any[] = c.options ?? [];
  const target = roleLabel.trim().toLowerCase();
  let chosen: FieldOption | null = null;
  for (const entry of rawOptions) {
    if (!entry || Number(entry.group_id) !== Number(roleGroup.id)) continue;
    const opt = roleGroup.options.find((fo) => Number(fo.id) === Number(entry.option_id));
    if (!opt) continue;
    const lbl = (opt.label || "").trim();
    if (!chosen) chosen = opt;
    if (lbl.toLowerCase() === target) { chosen = opt; break; }
  }
  if (!chosen) return null;
  const meta = chosen.metadata || {};
  return typeof meta.role_color === "string" ? legible(meta.role_color.trim()) : null;
}

function chunkRoster(roster: any[], size: number) {
  const filled = [...roster];
  while (filled.length < size) {
    filled.push(null);
  }
  const groups = [];
  for (let i = 0; i < filled.length; i += 5) {
    groups.push(filled.slice(i, i + 5));
  }
  return groups;
}

// --- SUB-COMPONENTS ---

function DraggableCard({ 
    character, role, availableRoles, isDragging, isDraggingFromHere, 
    onMouseDown, isInteractive, unavailable, unavailableReason, onRemove,
    selfRemovable, onSelfRemove,
    squadInfo, isSquadmateHovered, onMouseEnter, onMouseLeave,
    specLabel, specEntries, roleColor, classIconUrl,
    crossGuildState, wclScore
}: any) {
    if (!character) {
        return (
            <div 
                onMouseDown={isInteractive ? onMouseDown : undefined}
                className={`h-[48px] w-full border border-dashed border-[var(--c-line)] bg-[var(--c-bg-veil)] rounded flex items-center justify-center text-[10px] font-medium text-[var(--c-ink-4)] uppercase tracking-widest ${isInteractive ? 'hover:bg-[var(--c-sunken)] cursor-pointer' : ''}`}
            >
                Empty
            </div>
        );
    }

    const cName = character.name || "Unknown";
    const cClass = character.className || character.character_class_name || "Unknown";
    const color = getClassColor(cClass);

    return (
        <div 
            onMouseDown={isInteractive && !unavailable ? onMouseDown : undefined}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={`
                group/card relative h-[48px] w-full flex items-center gap-3 px-3 rounded border bg-[var(--c-surface)]
                ${isDragging ? 'opacity-0' : 'opacity-100'}
                ${isDraggingFromHere ? 'border-cyan-500/50 opacity-40 ring-1 ring-cyan-500/30' : 'border-[var(--c-line)] hover:border-[var(--c-line-strong)]'}
                ${unavailable ? 'opacity-75 saturate-50 border-red-500/20 cursor-not-allowed' : ''}
                ${isInteractive && !unavailable ? 'cursor-grab active:cursor-grabbing hover:bg-[var(--c-sunken)]' : ''}
                ${isSquadmateHovered ? 'bg-indigo-500/10' : ''}
                transition-all select-none
            `}
            style={isSquadmateHovered ? { outline: '2px dashed rgb(129, 140, 248)', outlineOffset: '-2px' } : undefined}
        >
            
            {classIconUrl ? (
                <img src={classIconUrl} alt="" className="h-7 w-7 flex-shrink-0 rounded border border-[var(--c-line)] object-cover bg-[var(--c-surface)]" onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.style.display='none'; if (el.nextElementSibling) (el.nextElementSibling as HTMLElement).style.display='block'; }} />
            ) : null}
            <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: color, display: classIconUrl ? 'none' : undefined }} />
            
            <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <div className="text-xs font-bold text-[var(--c-ink)] truncate leading-tight" style={{ color }}>{cName}</div>
                    {/* PUG Badge */}
                    {character.isPug && (
                        <span className="text-[9px] text-orange-700 dark:text-orange-300 bg-orange-500/20 px-1.5 py-0.5 rounded border border-orange-500/30 font-bold">PUG</span>
                    )}
                    {/* Cross-Guild Badge. Free agents have no guild to name, so they
                        get their own label instead of the generic "XG" fallback. */}
                    {character.isCrossGuild && (
                        character.isFreeAgent ? (
                            <span className="text-[9px] text-teal-700 dark:text-teal-300 bg-teal-500/20 px-1.5 py-0.5 rounded border border-teal-500/30 font-bold" title="Free agent · no guild">
                              FA
                            </span>
                        ) : (
                            <span className="text-[9px] text-purple-700 dark:text-purple-300 bg-purple-500/20 px-1.5 py-0.5 rounded border border-purple-500/30 font-bold" title={character.sourceGuildName || 'Another guild'}>
                              {character.sourceGuildTag || 'XG'}
                            </span>
                        )
                    )}
                    {/* Parse Badge — prominent, next to name */}
                    {wclScore !== undefined && wclScore !== null && (
                        <span
                            className="font-mono font-bold text-[13px] leading-none flex-shrink-0"
                            style={{ color: getParseColor(wclScore) }}
                            title={`Warcraft Logs parse: ${wclScore}`}
                        >
                            {wclScore}
                        </span>
                    )}
                </div>
                    {/* Show single role/spec if assigned, otherwise show all available specs */}
                    {role ? (
                        <span 
                          className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase"
                          style={{
                            backgroundColor: roleColor ? `${roleColor}20` : 'rgba(255,255,255,0.1)',
                            color: roleColor || 'rgba(255,255,255,0.7)'
                          }}
                        >
                          {specLabel || (role === "Melee DPS" ? "Melee" : role === "Ranged DPS" ? "Ranged" : role)}
                        </span>
                    ) : specEntries && specEntries.length > 0 ? (
                        <div className="flex gap-0.5">
                            {specEntries.slice(0, 3).map((se: any, i: number) => (
                                <span key={i} className="px-1 py-0.5 rounded text-[11px] leading-none bg-[var(--c-sunken-2)] text-[var(--c-ink-2)]" title={`${se.specLabel} (${se.roleLabel})`}>
                                    {getRoleIcon(se.roleLabel)}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div className="flex -space-x-1">
                            {(availableRoles || [character.role_label]).map((r: string, i: number) => (
                                <div key={i} className="text-[10px] opacity-60 bg-[var(--c-surface)] rounded-full border border-[var(--c-line)]" title={r}>
                                    {getRoleIcon(r)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="text-[10px] text-[var(--c-ink-3)] truncate leading-tight mt-0.5 flex items-center gap-1.5">
                    <span className="truncate">{cClass}</span>
                    {/* Why they can't be seated. Inline on this row rather than
                        floated over the corner, where it covered the spec icons. */}
                    {unavailable && (
                        <span className={`flex-shrink-0 text-[9px] font-bold uppercase tracking-wider ${
                            unavailableReason === "Not Confirmed" ? "text-orange-700 dark:text-orange-400" : "text-red-600 dark:text-red-400"
                        }`}>
                            · {unavailableReason || "Busy"}
                        </span>
                    )}
                    {squadInfo && (
                        <span className="flex items-center gap-0.5 text-[9px] text-indigo-700 dark:text-indigo-300 flex-shrink-0" title={`Squad: ${squadInfo.squadName}`}>
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            <span className="font-bold">{squadInfo.squadSize}</span>
                        </span>
                    )}
                </div>
            </div>

            {/* Cross-guild state dot — bottom-right corner.
                Themed tooltip matches the bg-[var(--c-surface)] border-[var(--c-line-strong)] pattern from ArcPage/RosterPage. */}
            {crossGuildState && (
                <span
                    className="group/xgstate absolute bottom-1 right-1 z-10"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <span
                        className="block w-2 h-2 rounded-full ring-2 ring-[var(--c-surface)]"
                        style={{ backgroundColor: crossGuildState.color }}
                    />
                    <span className="absolute bottom-full right-0 mb-1 px-3 py-1.5 bg-[var(--c-surface)] border border-[var(--c-line-strong)] rounded-lg text-xs text-[var(--c-ink)] whitespace-nowrap opacity-0 pointer-events-none group-hover/xgstate:opacity-100 transition-opacity z-[999] shadow-2xl font-normal normal-case tracking-normal">
                        {crossGuildState.label}
                    </span>
                </span>
            )}

            {/* Manager X (remove anyone) takes precedence; otherwise the player's
                OWN tile gets a self-leave X before the raid starts. Both look identical
                so the gesture is familiar — only the action + tooltip differ. */}
            {(isInteractive && onRemove) ? (
                <button
                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    className="absolute -top-2 -right-2 h-5 w-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-all shadow-lg z-20 hover:scale-110 hover:bg-red-600"
                    title="Remove from roster"
                >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
            ) : (selfRemovable && onSelfRemove) ? (
                <button
                    onClick={(e) => { e.stopPropagation(); onSelfRemove(); }}
                    className="absolute -top-2 -right-2 h-5 w-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-all shadow-lg z-20 hover:scale-110 hover:bg-red-600"
                    title="Leave this raid"
                >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
            ) : null}
        </div>
    );
}

function SuggestionCard({ candidate, onClick }: { candidate: HeuristicCharacter & { wclScore?: number; isConfirmed?: boolean }, onClick: () => void }) {
    const color = getClassColor(candidate.className);
    const parseColor = getParseColor(candidate.wclScore);

    return (
        <div className="flex items-center justify-between p-3 rounded bg-[var(--c-surface)] border border-[var(--c-line)] hover:bg-[var(--c-sunken)] transition-colors group">
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-1 h-8 rounded-full" style={{ backgroundColor: color }} />
                <div className="min-w-0">
                    <div className="text-sm font-bold truncate text-[var(--c-ink)]" style={{ color }}>{candidate.name}</div>
                    <div className="text-[10px] text-[var(--c-ink-3)] truncate flex items-center gap-2">
                        <span>{candidate.className}</span>
                        {candidate.wclScore !== undefined && (
                            <span className="px-1.5 rounded bg-[var(--c-sunken-2)] font-mono text-[9px]" style={{ color: parseColor }}>
                                {candidate.wclScore}
                            </span>
                        )}
                        {candidate.ilvl && <span className="text-[var(--c-ink-4)]">ilvl {candidate.ilvl}</span>}
                        <span className="text-green-700 dark:text-green-400 text-[9px]" title="Availability confirmed this week">✔ Confirmed</span>
                    </div>
                </div>
            </div>
            <button 
                onClick={onClick}
                className="px-3 py-1.5 rounded bg-cyan-600/20 border border-cyan-500/30 text-cyan-700 dark:text-cyan-300 text-[10px] font-bold uppercase hover:bg-cyan-500 hover:text-black transition-all"
            >
                Add
            </button>
        </div>
    );
}

/** Compute when a raid truly ends: last session start + duration. Falls back to anchorDate + 3hrs. */
function getRaidEndDate(sessions: any[] | undefined, anchorDate: Date): Date {
    if (!sessions || sessions.length === 0) {
      return new Date(anchorDate.getTime() + 3 * 60 * 60 * 1000); // default 3hr
    }
    let latestEnd = 0;
    for (const s of sessions) {
      const start = new Date(s.starts_at || s.startsAt).getTime();
      const dur = (s.duration || s.duration_minutes || s.durationMinutes || 180) * 60 * 1000;
      if (!isNaN(start)) {
        latestEnd = Math.max(latestEnd, start + dur);
      }
    }
    return latestEnd > 0 ? new Date(latestEnd) : new Date(anchorDate.getTime() + 3 * 60 * 60 * 1000);
}

function ScheduleCard({
    raid,
    onClick,
    onLootCouncil,
    showGame = false,
  }: {
    raid: ScheduledRaid;
    onClick: () => void;
    onLootCouncil?: () => void;
    // Only when the list mixes games (e.g. Anniversary + WoW Forever).
    showGame?: boolean;
  }) {
    const userTz = useTimezone();
    const isAssigned = !!raid.myCharName;
    const anchorIso = raid.anchorDate.toISOString();
    const z = DateTime.fromISO(anchorIso, { zone: "utc" }).setZone(userTz);
    const month = z.isValid ? z.toFormat("MMM").toUpperCase() : "";
    const dayNum = z.isValid ? z.day : "";
    const weekdayShort = z.isValid ? z.toFormat("ccc").toUpperCase() : "";
    const startTimeStr = formatRaidTime(anchorIso, userTz).time;
    const endTimeStr = formatRaidTime(raid.endDate.toISOString(), userTz).time;

    // Guild Logo Fallback Component
    const GuildFallback = () => (
        <div className="h-5 w-5 shrink-0 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-[10px] text-indigo-700 dark:text-indigo-300 font-bold">
            {raid.hostedByGuild.charAt(0).toUpperCase()}
        </div>
    );

    return (
        <div 
            onClick={onClick}
            className="group relative flex h-36 w-full cursor-pointer overflow-hidden rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)] transition-all duration-200 hover:-translate-y-px hover:border-[var(--c-accent-line)] hover:shadow-[var(--c-shadow-lg)]"
        >
            {/* LEFT: Cinematic Image with 0-100% Fade */}
            <div className="relative w-1/3 max-w-[280px] shrink-0 overflow-hidden">
                {raid.raidImageUrl ? (
                    <div 
                        className="absolute inset-0 h-full w-full"
                        style={{ 
                            // Gradual fade from solid to transparent
                            maskImage: 'linear-gradient(to right, black 0%, black 30%, transparent 100%)',
                            WebkitMaskImage: 'linear-gradient(to right, black 0%, black 30%, transparent 100%)'
                        }}
                    >
                        <img 
                            src={raid.raidImageUrl} 
                            alt="" 
                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    </div>
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/10 to-transparent" />
                )}
                
                {/* Embedded Date Badge */}
                <div className="absolute top-3 left-3 flex min-w-[52px] flex-col items-center justify-center rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-2 py-2 shadow-[var(--c-shadow)]">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-[var(--c-accent-ink)] leading-none">{month}</span>
                    <span className="text-2xl text-[var(--c-ink)] leading-tight my-0.5" style={DISPLAY}>{dayNum}</span>
                    <span className="text-[9px] font-bold text-[var(--c-ink-3)] uppercase leading-none">{weekdayShort}</span>
                </div>
            </div>

            {/* CENTER: Info & Guild */}
            <div className="relative flex flex-1 flex-col justify-center py-4 pr-4 -ml-4 z-10">
            <div className="flex items-center gap-2 mb-2">
                    {showGame && raid.gameVersion && (
                        <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-[var(--c-accent-line)] bg-[var(--c-accent-soft)] text-[var(--c-accent-ink)]">
                            {raid.gameVersion}
                        </span>
                    )}
                    {raid.isPublished === false && (
                        <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                            Draft
                        </span>
                    )}
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                        raid.difficulty === 'Heroic' ? 'border-purple-500/30 bg-purple-500/10 text-purple-700 dark:text-purple-300' :
                        raid.difficulty === 'Mythic' ? 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300' :
                        'border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-3)]'
                    }`}>
                        {raid.difficulty}
                    </span>
                    <span className="text-[11px] tabular-nums text-[var(--c-accent-ink)] font-semibold flex items-center gap-1">
                        <svg className="w-3 h-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        {startTimeStr} - {endTimeStr}
                    </span>
                </div>

                <h3 className="text-xl text-[var(--c-ink)] transition-colors group-hover:text-[var(--c-accent-ink)] truncate" style={DISPLAY}>
                    {raid.raidName}
                </h3>

                <div className="flex items-center gap-2 mt-2">
                    {raid.guildLogo ? (
                        <img 
                            src={raid.guildLogo} 
                            alt="" 
                            className="h-5 w-5 shrink-0 rounded-full border border-[var(--c-line)] bg-[var(--c-sunken-2)] object-cover" 
                            onError={(e) => { e.currentTarget.style.display='none'; }} 
                        />
                    ) : <GuildFallback />}
                    
                    <span className="text-xs font-medium text-[var(--c-ink-3)] truncate">{raid.hostedByGuild}</span>
                    <span className="text-xs text-[var(--c-ink-4)]">•</span>
                    <span className="text-xs text-[var(--c-ink-3)] shrink-0">{raid.size}-Man</span>
                </div>
            </div>

            {/* RIGHT: Status & Character Avatar */}
            <div className="flex w-48 shrink-0 flex-col justify-center border-l border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] p-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-[var(--c-ink-4)]">Assignment</span>
                    <div className={`h-1.5 w-1.5 rounded-full ${isAssigned ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]' : 'bg-[var(--c-sunken-2)]'}`} />
                </div>

                {raid.srEnabled && (
                    <div className={`mb-2 flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 rounded-lg border ${
                        (raid.srMyCount || 0) >= (raid.srMax || 1)
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                            : raid.srLocked
                            ? 'bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400'
                            : 'bg-purple-500/10 border-purple-500/20 text-purple-700 dark:text-purple-300'
                    }`}>
                        <span>&#x1F4DC;</span>
                        <span>SR {raid.srMyCount || 0}/{raid.srMax}</span>
                        {raid.srLocked && <span>&#x1F512;</span>}
                    </div>
                )}

                {onLootCouncil && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onLootCouncil();
                    }}
                    className="mb-2 w-full rounded-lg border border-violet-500/25 bg-violet-500/10 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-violet-800 dark:text-violet-200 hover:bg-violet-500/20 hover:border-violet-400/40 transition-colors"
                  >
                    Loot council
                  </button>
                )}

                {isAssigned ? (
                    <div className="flex items-center gap-3">
                        {/* Character Avatar or Role Icon */}
                        <div className="relative h-9 w-9 shrink-0">
                            {raid.myCharImage ? (
                                <>
                                    <img src={raid.myCharImage} alt="" className="h-full w-full rounded-md border border-[var(--c-line)] object-cover" />
                                    <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-[var(--c-surface)] border border-[var(--c-line)] flex items-center justify-center text-[9px]">
                                        {getRoleIcon(raid.myCharRole || "")}
                                    </div>
                                </>
                            ) : (
                                <div className="h-full w-full rounded-md bg-[var(--c-surface)] border border-[var(--c-line)] flex items-center justify-center text-lg">
                                    {getRoleIcon(raid.myCharRole || "")}
                                </div>
                            )}
                        </div>
                        
                        <div className="min-w-0 overflow-hidden">
                            <div className="text-sm font-bold truncate text-[var(--c-ink)] leading-tight" style={{ color: getClassColor(raid.myCharClass) }}>
                                {raid.myCharName}
                            </div>
                            <div className="text-[10px] text-[var(--c-ink-3)] truncate leading-tight mt-0.5">
                                {raid.myCharClass}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 opacity-40">
                         <div className="h-9 w-9 rounded-md border border-dashed border-[var(--c-line-strong)] flex items-center justify-center text-[var(--c-ink-4)]">
                            +
                         </div>
                         <div className="flex flex-col">
                            <span className="text-xs font-bold text-[var(--c-ink-3)]">Sign up</span>
                            <span className="text-[9px] text-[var(--c-ink-4)]">Available</span>
                         </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- MAIN COMPONENT ---

export type CrossGuildNotice = {
    id: string;
    user_id: string;
    character_id: string;
    source_guild_id: string | null;
    notified_at: string | null;
    notified_via: string | null;
    loot_info_sent: string | null;
    discord_invite_sent: boolean | null;
    response: 'accepted' | 'declined' | null;
    response_at: string | null;
    created_at: string;
    character_name: string;
    class_name: string | null;
    user_display_name: string;
    discord_id: string | null;
    source_guild_name: string | null;
    source_guild_tag: string | null;
    /** True for a seat dropped since the last save: mirrored client-side so the
     *  invite panel is live immediately. Sending a DM saves the roster first,
     *  which is what turns it into a real row. Never set on rows from the server. */
    pending?: boolean;
};

// Removal reasons for the drag-to-bin zones and post-removal toast. Codes match
// auth/roster_event_service.js REMOVAL_REASONS; countsAgainst defaults live server-side.
const REMOVAL_REASON_OPTIONS: { code: string; label: string }[] = [
  { code: 'bench',     label: 'Bench' },
  { code: 'no_show',   label: 'No-show' },
  { code: 'asked_out', label: 'Asked out' },
  { code: 'replaced',  label: 'Replaced' },
  { code: 'other',     label: 'Other' },
];

// System-set removal reasons that are shown in the timeline but are not
// drag-to-bin options (REMOVAL_REASON_OPTIONS is index-mapped to bin zones).
const SYSTEM_REMOVAL_REASON_LABELS: Record<string, string> = {
  open_bench_pickup: 'Taken via Open Bench',
};

// The small uppercase label + amber hairline that heads a page on the
// Alignment sign up form, reused so the planner reads as the same product.
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--c-accent-ink)]">{children}</div>
    </div>
  );
}

function SectionTitle({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div className="mb-4 flex items-baseline gap-3 px-1">
      <h3 className="text-lg text-[var(--c-ink)]" style={DISPLAY}>{children}</h3>
      {count !== undefined && <span className="text-sm font-semibold text-[var(--c-ink-4)]">{count}</span>}
      <div className="ml-2 h-px flex-1 bg-gradient-to-r from-[var(--c-line)] to-transparent" />
    </div>
  );
}

export default function PlannerPage(props: PlannerPageProps) {
  return (
    <ThemeProvider>
      <PlannerPageInner {...props} />
    </ThemeProvider>
  );
}

function PlannerPageInner({
    guilds,
    characters,
    accountAvailability,
    adminViewGuildId = null,
}: PlannerPageProps) {
  const { themeClass, onLight, theme } = useTheme();
  legible = onLight;
  // Every view renders inside this panel so the palette (and the Inter type
  // the Alignment pages use) covers the whole planner. A view that has not
  // been moved onto theme tokens yet (ARC) passes `legacyDark`, which pins it
  // to the dark palette it was built for.
  const themed = (node: React.ReactNode, legacyDark = false) => (
    <PaletteContext.Provider value={legacyDark ? "dark" : theme}>
      <div
        className={`${legacyDark ? "lfm-theme lfm-dark" : themeClass} rounded-3xl bg-[var(--c-bg)] p-4 sm:p-6 lg:p-8 antialiased`}
        style={BODY}
      >
        {node}
      </div>
    </PaletteContext.Provider>
  );
  const userTz = useTimezone();
    const { confirm } = useConfirm();
    const { showToast } = useToast();

    // Guild emblem behind the planner header. First guild with an emblem wins,
    // so a multi-guild officer gets one stable backdrop instead of a flicker
    // between them. No emblem uploaded = the plain header as before.
    const backdropLogoUrl = useMemo(() => {
      const logo = guilds.find((g) => g.logoUrl)?.logoUrl;
      if (!logo) return null;
      return logo.startsWith("http") ? logo : `${API_URL}${logo}`;
    }, [guilds]);

    // Cross-guild notice state — populated from GET /raids/:id/detail,
    // PUT /raids/:id/assignments, and POST /raids/:id/notify-cross-guild.
    const [crossGuildNotices, setCrossGuildNotices] = useState<CrossGuildNotice[]>([]);
    const [crossGuildCollapsed, setCrossGuildCollapsed] = useState(false);
    const [notifyModalOpen, setNotifyModalOpen] = useState(false);
    const [notifyModalUserIds, setNotifyModalUserIds] = useState<string[]>([]);
    const [notifyLootSystem, setNotifyLootSystem] = useState<'soft_reserve' | 'soft_reserve_hard_reserve' | 'dkp' | 'loot_council'>('soft_reserve');
    const [notifyLoading, setNotifyLoading] = useState(false);
  
    // NEW: Internal raids state
  const [raids, setRaids] = useState<ScheduledRaid[]>([]);
  const [raidsLoading, setRaidsLoading] = useState(false);
  const [raidsError, setRaidsError] = useState<string | null>(null);

// Fetch raids function
const fetchRaids = async () => {
    setRaidsLoading(true);
    setRaidsError(null);
    
    try {
      const res = await fetch(
        `${AUTH_BASE_URL}/my/raids${adminViewGuildId ? `?guildId=${encodeURIComponent(adminViewGuildId)}` : ''}`,
        { credentials: 'include' }
      );
      
      if (!res.ok) throw new Error('Failed to fetch raids');
      
      const data = await res.json();
      
      const transformedRaids: ScheduledRaid[] = data.map((r: any) => {
        const firstSession = r.sessions?.[0];
        const anchorDate = firstSession?.starts_at ? new Date(firstSession.starts_at) : new Date();
        const raidMeta = r.metadata || {};
        const guildMeta = r.guild_metadata || {};
        const myAssignment = r.my_assignment || {};
        
        // Resolve Logo
        let gLogo = r.guild_logo_url || guildMeta.logo_url || null;
        if (gLogo && !gLogo.startsWith("http")) gLogo = `${API_URL}${gLogo}`;

        // Resolve Char Image
        let cImg = myAssignment.avatar_url || myAssignment.image_url || myAssignment.render_url || null;
        if (cImg && !cImg.startsWith("http")) cImg = `${API_URL}${cImg}`;
        
        return {
            id: r.id,
            gameVersion: shortGameName(r.game_name),
            region: raidMeta.region || guildMeta.region || 'US',
            realm: raidMeta.realm || r.guild_server_name || 'Unknown',
            raidName: r.raid_name,
            difficulty: raidMeta.difficulty || 'Normal',
            size: raidMeta.size || 10,
            sessions: r.sessions,
            anchorDate: anchorDate,
            endDate: getRaidEndDate(r.sessions, anchorDate),
            localTimeLabel: formatRaidTime(anchorDate.toISOString(), userTz).time,
            roleSummary: raidMeta.role_summary || '',
            raidLeader: r.raid_leader?.character_name || raidMeta.raid_leader || '',
            lootMaster: r.loot_master?.character_name || raidMeta.loot_master || '',
            hostedByGuild: r.guild_name || (raidMeta.auto_generated ? 'Automatic Raid' : 'Unknown Guild'),
            guild_id: r.guild_id,
            guildLogo: gLogo,
            seriesId: r.series_id,
            
            // Assignment
            myCharName: myAssignment.name,
            myCharClass: myAssignment.class_name,
            myCharRole: myAssignment.role,
            myCharImage: cImg,
            
            // Raid image
            raidImageUrl: r.raid_image_url || null,

            // Soft Reserve info
            srEnabled: !!r.soft_reserve_info?.enabled,
            srLocked: r.soft_reserve_info?.locked || false,
            srMyCount: r.soft_reserve_info?.my_count || 0,
            srMax: r.soft_reserve_info?.max_reserves || 0,
            srMyItems: r.soft_reserve_info?.my_items || null,
            isPublished: r.is_published !== false,
          };
      });
      
      setRaids(transformedRaids);
      
    } catch (e: any) {
      console.error('Failed to fetch raids:', e);
      setRaidsError(e.message || 'Failed to load raids');
    } finally {
      setRaidsLoading(false);
    }
  };

  // Fetch on mount + check for pending raid/SR navigation
  useEffect(() => {
    fetchRaids();
    const pendingSr = sessionStorage.getItem('pending_sr_raid');
    if (pendingSr) {
      sessionStorage.removeItem('pending_sr_raid');
      setActiveRaidId(pendingSr);
      setMode("detail");
      openRaidDetail(pendingSr).then(() => {
        setMode("soft-reserve");
      });
    } else {
      const pendingOpen = sessionStorage.getItem('pending_open_raid');
      if (pendingOpen) {
        sessionStorage.removeItem('pending_open_raid');
        setActiveRaidId(pendingOpen);
        setMode("detail");
        openRaidDetail(pendingOpen);
      }
    }
  }, []);

  // Expose refresh function globally
  useEffect(() => {
    window.refreshPlannerRaids = fetchRaids;
    return () => { delete window.refreshPlannerRaids; };
  }, []);

  const [mode, setMode] = useState<"list" | "create" | "detail" | "soft-reserve" | "assignments" | "arc" | "loot-council">("list");
  const [lootCouncilRaidId, setLootCouncilRaidId] = useState<string | null>(null);
  const [lootCouncilGuildId, setLootCouncilGuildId] = useState<string | null>(null);
  const [lootCouncilRaidName, setLootCouncilRaidName] = useState<string>("");
  const [lootCouncilReturnMode, setLootCouncilReturnMode] = useState<"list" | "detail">("list");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detail view state
  const [activeRaidId, setActiveRaidId] = useState<string | null>(null);
  const [activeRaid, setActiveRaid] = useState<any | null>(null);

  const openLootCouncilFromRaid = useCallback((raid: ScheduledRaid, returnTo: "list" | "detail") => {
    setLootCouncilReturnMode(returnTo);
    setLootCouncilRaidId(raid.id);
    setLootCouncilGuildId(raid.guild_id);
    setLootCouncilRaidName(raid.raidName);
    setMode("loot-council");
  }, []);

  const [activeAssignments, setActiveAssignments] = useState<any[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [editRaidLeader, setEditRaidLeader] = useState<string>("");
  const [editLootMaster, setEditLootMaster] = useState<string>("");
  
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<any[]>([]); // Store candidates
  const [srStatus, setSrStatus] = useState<{ enabled: boolean; myCount: number; max: number; locked: boolean; myItems: string[] } | null>(null);

  // Auto-Fill & Suggestions State
  const [guildRosterMap, setGuildRosterMap] = useState<Record<string, any[]>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [ignoreAvailability, setIgnoreAvailability] = useState(false);
  
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionGroup[]>([]);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [pugSrCopied, setPugSrCopied] = useState(false);

  // Edit Raid Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInfo, setEditInfo] = useState<any | null>(null);
  const [editName, setEditName] = useState("");
  const [editRaidOptionId, setEditRaidOptionId] = useState<number | null>(null);
  const [editChainedRaidOptionIds, setEditChainedRaidOptionIds] = useState<number[]>([]);
  const [editSessions, setEditSessions] = useState<{ date: string; startTime: string; endTime: string }[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const [forceConfirmData, setForceConfirmData] = useState<any>(null);
  const [originalEditSessions, setOriginalEditSessions] = useState<{ date: string; startTime: string; endTime: string }[]>([]);
  const [showScheduleConfirm, setShowScheduleConfirm] = useState(false);
  // Soft reserve removal (from edit modal)
  const [srRemoving, setSrRemoving] = useState(false);
  const [confirmRemoveSr, setConfirmRemoveSr] = useState(false);

  // Role Selection State
  const [roleSelection, setRoleSelection] = useState<{ candidate: any, targetIndex: number } | null>(null);

  // Drag State
  const [dragOrigin, setDragOrigin] = useState<{ type: 'bench' | 'slot' | 'benchseat', id: string, index?: number, role?: string } | null>(null);
  const [dragPos, setDragPos] = useState<{ x: number, y: number } | null>(null);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const benchZoneRef = useRef<HTMLDivElement | null>(null);

  // Roster removal tracking. Removals are staged locally (like the roster itself)
  // and sent as `removals` alongside the PUT on Save so the server can log WHY each
  // player was dropped. Reasons come from the drag-to-bin zones or the post-removal
  // toast; anything untagged goes out as 'unspecified' (neutral for attendance).
  const [pendingRemovals, setPendingRemovals] = useState<Record<string, { characterId: string; characterName: string; reasonCode: string; rows: any[] }>>({});
  const [removalPrompt, setRemovalPrompt] = useState<{ characterIds: string[]; label: string } | null>(null);
  const binChipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [binHoverIdx, setBinHoverIdx] = useState<number | null>(null);

  // Roster change timeline (guild_raid_roster_event log) shown on raid detail.
  const [rosterEventLog, setRosterEventLog] = useState<{ events: any[]; canManage: boolean } | null>(null);
  const [rosterLogOpen, setRosterLogOpen] = useState(false);
  
  // Squad state
  const [squadLookup, setSquadLookup] = useState<SquadLookup>({});
  const [hoveredSquadId, setHoveredSquadId] = useState<string | null>(null);
  
  // Squad intercept modal state
  const [squadIntercept, setSquadIntercept] = useState<{
    triggerCharId: string;
    triggerCharName: string;
    triggerCharClassName: string;
    triggerCharRoles: string[];
    triggerCharUserId: string;
    squadInfo: SquadInfo;
    targetIndex: number;
    availableSquadmates: { id: string; name: string; className: string; roles: string[]; userId: string }[];
  } | null>(null);
  
  // Squad selection state: { userId: { charId, roleLabel } }
  const [squadSelections, setSquadSelections] = useState<Record<string, { charId: string; roleLabel: string }>>({});
  // Whether user has acknowledged they want to split the squad
  const [allowSquadSplit, setAllowSquadSplit] = useState(false);

  // Game fields & composition tags for spec resolution + buff coverage
  const [currentGameFields, setCurrentGameFields] = useState<FieldGroup[] | null>(null);
  const [compositionTags, setCompositionTags] = useState<CompositionTag[]>([]);
  const [buffPanelOpen, setBuffPanelOpen] = useState(false);
  // Roster Pool status buckets ("In Another Raid", "Not Confirmed", ...). Collapsed by
  // default so the rail reads as a status summary; a bucket opens on click to drag from.
  const [openBuckets, setOpenBuckets] = useState<Set<string>>(new Set());
  const [hoveredSpecTags, setHoveredSpecTags] = useState<Set<string>>(new Set());

  const sortedRaids = useMemo(() => [...raids].sort((a,b) => a.anchorDate.getTime() - b.anchorDate.getTime()), [raids]);

  // --- Split Upcoming vs Passed ---
  const [showPassed, setShowPassed] = useState(false);
  
  const { upcomingRaids, liveRaids, passedRaids } = useMemo(() => {
    const now = new Date();
    const upcoming: ScheduledRaid[] = [];
    const live: ScheduledRaid[] = [];
    const passed: ScheduledRaid[] = [];

    sortedRaids.forEach((r) => {
      const nowMs = now.getTime();
      const startMs = r.anchorDate.getTime();
      const endMs = r.endDate.getTime();

      if (nowMs >= endMs) {
        passed.push(r);
      } else if (nowMs >= startMs && nowMs < endMs) {
        live.push(r);
      } else {
        upcoming.push(r);
      }
    });

    // Sort passed raids descending (most recent passed raid at top)
    return { upcomingRaids: upcoming, liveRaids: live, passedRaids: passed.reverse() };
  }, [sortedRaids]);

  const [me, setMe] = useState<any | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);

  useEffect(() => {
    fetch("/me", { credentials: "include" })
        .then(res => res.ok ? res.json() : null)
        .then(data => { setMe(data); setMeLoaded(true); })
        .catch(() => setMeLoaded(true));
  }, []);

  // ---- Loot Queue Arena (discreet easter-egg mini-game) ----
  // Hosting a session = referee. Nothing here touches the DB or persists.
  const [arenaHostSession, setArenaHostSession] = useState<string | null>(null);

  const arenaIdentity = useMemo(() => {
    const c: any = characters && characters[0];
    return {
      id: me?.id ? String(me.id) : (c?.id ? String(c.id) : `host_${Math.random().toString(36).slice(2)}`),
      name: (c?.name as string) || me?.username || "Host",
      className: (c?.className as string) || null,
    };
  }, [characters, me]);

  // Host-side mapping: planner's already-loaded `candidates` → ArenaPlayer[].
  // ZERO new fetches — reuse raid candidates, then guild rosters, then own chars.
  // name + className required; wclScore → parse; resolved role → role; render_url → icon.
  // *** ilvl is NEVER mapped; BiS is not mapped (unavailable host-side). ***
  const arenaPool = useMemo<ArenaPlayer[]>(() => {
    const out: ArenaPlayer[] = [];
    const seen = new Set<string>();
    const push = (r: any) => {
      if (!r) return;
      const id = String(r.characterId ?? r.id ?? r.character_id ?? "");
      const name = r.name ?? r.character_name;
      if (!id || !name || seen.has(id)) return;
      const className = r.metadata?.class_name ?? r.className ?? r.character_class_name;
      if (!className) return;
      seen.add(id);
      const roles = resolveRoleLabels(r, currentGameFields);
      // Every spec the player plays → the arena rolls one at random per card.
      const specEntries = resolveSpecEntries(r, currentGameFields);
      const specs = specEntries
        .filter((e) => e.specLabel)
        .map((e) => ({ label: e.specLabel, role: e.roleLabel }));
      out.push({
        id,
        name: String(name),
        className: String(className),
        role: specs[0]?.role ?? roles[0] ?? null,
        specs: specs.length ? specs : undefined,
        parse: r.metadata?.wcl_perf?.score ?? r.wclScore ?? null,
        icon: r.metadata?.render_url ?? r.render_url ?? null,
      });
    };
    candidates.forEach(push);
    Object.values(guildRosterMap).forEach((list) => (list || []).forEach(push));
    (characters || []).forEach(push);
    return out;
  }, [candidates, guildRosterMap, characters, currentGameFields]);

  // Fetch squad info when candidates or active raid changes
  useEffect(() => {
    if (!activeRaidId || !activeRaid) return;
    const sourceRoster = candidates.length > 0 ? candidates : (guildRosterMap[activeRaid.guild_id] || []);
    const uniqueUserIds = Array.from(new Set(sourceRoster.map((c: any) => c.userId).filter(Boolean)));
    if (uniqueUserIds.length > 0) {
      fetch(`/api/squads/by-users?userIds=${uniqueUserIds.join(',')}`, { credentials: "include" })
        .then(r => r.ok ? r.json() : { squads: {} })
        .then(data => setSquadLookup(data.squads || {}))
        .catch(() => setSquadLookup({}));
    } else {
      setSquadLookup({});
    }
  }, [activeRaidId, activeRaid?.guild_id, candidates.length]);

  useEffect(() => {
    if (guilds.length === 0) return;
    guilds.forEach(g => {
      if (guildRosterMap[g.id]) return;
      fetch(`/guilds/${g.id}`, { credentials: "include" })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && Array.isArray(data.roster)) {
            setGuildRosterMap(prev => ({ ...prev, [g.id]: data.roster }));
          }
        }).catch(() => {});
    });
  }, [guilds]);

  // Drag Logic
  useEffect(() => {
    if (!dragOrigin) return;
    const hitBinChip = (x: number, y: number) => {
        let idx = -1;
        binChipRefs.current.forEach((el, i) => {
            if (el) {
                const r = el.getBoundingClientRect();
                if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) idx = i;
            }
        });
        return idx;
    };
    const move = (e: MouseEvent) => {
        setDragPos({ x: e.clientX, y: e.clientY });
        if (dragOrigin.type === 'slot' || dragOrigin.type === 'benchseat') {
            setBinHoverIdx(prev => {
                const idx = hitBinChip(e.clientX, e.clientY);
                return idx === prev ? prev : (idx === -1 ? null : idx);
            });
        }
    };
    const up = (e: MouseEvent) => {
        const x = e.clientX;
        const y = e.clientY;
        let targetSlot = -1;
        slotRefs.current.forEach((el, idx) => {
            if (el) {
                const r = el.getBoundingClientRect();
                if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) targetSlot = idx;
            }
        });
        let overBench = false;
        if (benchZoneRef.current) {
            const br = benchZoneRef.current.getBoundingClientRect();
            if (x >= br.left && x <= br.right && y >= br.top && y <= br.bottom) overBench = true;
        }
        // Bin dock reason zones (only live while dragging a rostered player).
        const binIdx = (dragOrigin.type === 'slot' || dragOrigin.type === 'benchseat') ? hitBinChip(x, y) : -1;

        if (binIdx !== -1) {
            const reason = REMOVAL_REASON_OPTIONS[binIdx].code;
            if (dragOrigin.type === 'slot') handleUnassign(dragOrigin.index!, reason);
            else handleBenchRemove(dragOrigin.id, reason);
        } else if (targetSlot !== -1) {
            handleDrop(dragOrigin, targetSlot);
        } else if (overBench) {
            handleBenchDrop(dragOrigin);
        } else if (dragOrigin.type === 'slot') {
            handleUnassign(dragOrigin.index!);
        } else if (dragOrigin.type === 'benchseat') {
            handleBenchRemove(dragOrigin.id);
        }
        setDragOrigin(null);
        setDragPos(null);
        setBinHoverIdx(null);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [dragOrigin]);

  // --- Roster removal reason tracking (drag-to-bin / toast) ---

  // Stage removal reasons for characters just dropped from the roster. Untagged
  // removals open the non-blocking toast so the RL can tag (or ignore) them.
  const recordRemovals = (rows: any[], reasonCode: string) => {
    const valid = rows.filter(r => r && r.character_id);
    if (valid.length === 0) return;
    setPendingRemovals(prev => {
        const next = { ...prev };
        valid.forEach(r => {
            next[r.character_id] = {
                characterId: r.character_id,
                characterName: r.character_name || r.name || 'Player',
                reasonCode,
                rows: [r],
            };
        });
        return next;
    });
    if (reasonCode === 'unspecified') {
        const label = valid.length === 1 ? (valid[0].character_name || valid[0].name || 'Player') : `${valid.length} players`;
        setRemovalPrompt({ characterIds: valid.map(r => r.character_id), label });
    }
  };

  const setRemovalReason = (characterIds: string[], reasonCode: string) => {
    setPendingRemovals(prev => {
        const next = { ...prev };
        characterIds.forEach(id => { if (next[id]) next[id] = { ...next[id], reasonCode }; });
        return next;
    });
    setRemovalPrompt(null);
  };

  // Undo a just-staged removal: restore the assignment rows (original seat if still
  // free, otherwise the bench) and forget the pending reason.
  const undoRemoval = (characterIds: string[]) => {
    setActiveAssignments(prev => {
        const next = [...prev];
        characterIds.forEach(id => {
            const rec = pendingRemovals[id];
            if (!rec) return;
            rec.rows.forEach(row => {
                if (next.some(a => a.character_id === row.character_id)) return;
                const meta = { ...(row.metadata || {}) };
                const seatTaken = meta.seat_index !== undefined && meta.seat_index !== null
                    && next.some(a => Number(a.metadata?.seat_index) === Number(meta.seat_index));
                if (seatTaken) { delete meta.seat_index; meta.is_bench = true; }
                next.push({ ...row, metadata: meta, is_required: meta.is_bench ? false : row.is_required });
            });
        });
        return next;
    });
    setPendingRemovals(prev => {
        const next = { ...prev };
        characterIds.forEach(id => delete next[id]);
        return next;
    });
    setRemovalPrompt(null);
  };

  // If a removed character comes back onto the roster before Save, the staged
  // removal is moot — drop it (and close the toast if it pointed at them).
  useEffect(() => {
    const onRoster = new Set(activeAssignments.map((a: any) => a.character_id));
    const stale = Object.keys(pendingRemovals).filter(id => onRoster.has(id));
    if (stale.length === 0) return;
    setPendingRemovals(prev => {
        const next = { ...prev };
        stale.forEach(id => delete next[id]);
        return next;
    });
    setRemovalPrompt(prev => prev && prev.characterIds.some(id => stale.includes(id)) ? null : prev);
  }, [activeAssignments]);

  // Toast auto-dismiss. Ignoring it is fine: the removal stays 'unspecified'.
  useEffect(() => {
    if (!removalPrompt) return;
    const t = setTimeout(() => setRemovalPrompt(null), 10000);
    return () => clearTimeout(t);
  }, [removalPrompt]);

  // Officer correction of a logged removal reason (roster changes timeline).
  async function updateRosterEventReason(eventId: string, reasonCode: string) {
    if (!activeRaidId) return;
    try {
        const res = await fetch(`/raids/${encodeURIComponent(activeRaidId)}/roster-events/${encodeURIComponent(eventId)}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reasonCode }),
        });
        if (!res.ok) return;
        const data = await res.json();
        setRosterEventLog(prev => prev ? {
            ...prev,
            events: prev.events.map(e => e.id === eventId
                ? { ...e, reason_code: data.event.reason_code, counts_against: data.event.counts_against }
                : e),
        } : prev);
    } catch { /* leave the row as-is */ }
  }

  // Cross-guild context rides along on the local assignment so the invite panel can
  // register an out-of-guild player the moment they're placed, before any save. The
  // PUT payload only reads character_id / role_label / is_required / metadata, so
  // these fields are inert on the save path.
  const crossGuildFields = (c: any) => ({
    isCrossGuild: !!c?.isCrossGuild,
    isFreeAgent: !!c?.isFreeAgent,
    sourceGuildName: c?.sourceGuildName || null,
    sourceGuildTag: c?.sourceGuildTag || null,
  });

  const handleDrop = (origin: any, targetIndex: number) => {
    const next = [...activeAssignments];
    
    // Case 1: Swapping existing slots (No prompt needed)
    if (origin.type === 'slot') {
        const oldIdx = next.findIndex(a => Number(a.metadata?.seat_index) === origin.index);
        if (oldIdx !== -1) {
            const targetExistingIdx = next.findIndex(a => Number(a.metadata?.seat_index) === targetIndex);
            if (targetExistingIdx !== -1) next[targetExistingIdx].metadata.seat_index = origin.index;
            next[oldIdx].metadata.seat_index = targetIndex;
            setActiveAssignments(next);
            setIsDirty(true);
        }
        return;
    } 

    // Case 1b: Promote a true-bench player into a seat. Auto-corrects the row
    // (drops is_bench/bench_index, gains seat_index). Any current occupant is
    // swapped down to the bench.
    if (origin.type === 'benchseat') {
        const benchIdx = next.findIndex(a => ((a.id && a.id === origin.id) || a.character_id === origin.id) && a.metadata?.is_bench);
        if (benchIdx === -1) return;
        const occupantIdx = next.findIndex(a => Number(a.metadata?.seat_index) === targetIndex);
        if (occupantIdx !== -1 && occupantIdx !== benchIdx) {
            const occMeta = { ...(next[occupantIdx].metadata || {}) };
            delete occMeta.seat_index;
            occMeta.is_bench = true;
            next[occupantIdx] = { ...next[occupantIdx], is_required: false, metadata: occMeta };
        }
        const benchMeta = { ...(next[benchIdx].metadata || {}) };
        delete benchMeta.is_bench;
        delete benchMeta.bench_index;
        benchMeta.seat_index = targetIndex;
        next[benchIdx] = { ...next[benchIdx], is_required: true, metadata: benchMeta };
        setActiveAssignments(next);
        setIsDirty(true);
        return;
    }
    
    // Case 2: Dropping from Bench (Prompt for role)
    // Look up in candidates first (contains full metadata including roles), then fallback to guild roster
    let charData = candidates.find(c => c.characterId === origin.id) || 
                   guildRosterMap[activeRaid.guild_id]?.find((c: any) => c.characterId === origin.id);
    
    if (!charData) {
        charData = {
            id: origin.id, // Normalized to 'id' for internal use
            characterId: origin.id,
            name: "External",
            userId: "unknown",
            metadata: { class_name: "Unknown", role_labels: ["DPS"] }
        };
    }

    // Check if this character is in a squad
    const charUserId = charData.userId || charData.ownerId;
    const charSquadInfo = charUserId ? squadLookup[charUserId] : null;
    
    if (charSquadInfo && charSquadInfo.squadSize > 1) {
        // Find other squad members who are available on the bench
        const sourceRoster = candidates.length > 0 ? candidates : (guildRosterMap[activeRaid.guild_id] || []);
        const seatedCharIds = new Set(activeAssignments.map(a => a.character_id));
        const seatedUserIds = new Set<string>();
        activeAssignments.forEach(a => {
            const c = sourceRoster.find((x: any) => x.characterId === a.character_id);
            if (c?.userId) seatedUserIds.add(c.userId);
        });
        
        // Compute raid windows for availability check (matches bench logic)
        const raidWindows = sessionWindowsForRaid(activeRaid, userTz);

        const availableSquadmates = sourceRoster.filter((c: any) => {
            if (c.characterId === charData.characterId) return false; // Exclude the dragged character
            if (!c.userId || !charSquadInfo.memberUserIds.includes(c.userId)) return false; // Not in squad
            if (seatedCharIds.has(c.characterId)) return false; // Already seated
            if (seatedUserIds.has(c.userId)) return false; // User already has a char seated
            if (c.isInterested === false) return false; // Not interested
            if (c.isLockedOut === true) return false; // Locked out this instance
            if (c.isBusy === true) return false; // In another raid at this time (bilocation)
            if (!ignoreAvailability && c.isConfirmed === false) return false; // Not confirmed availability
            if (!ignoreAvailability && !isCharacterAvailable({ id: c.characterId, availabilityBits: c.userAvailability } as any, raidWindows)) return false; // Time unavailable
            return true;
        }).map((c: any) => ({
            id: c.characterId,
            name: c.name,
            className: c.metadata?.class_name || c.className || "Unknown",
            roles: c.roles || c.metadata?.role_labels || ["DPS"],
            userId: c.userId
        }));
        
        // If there are available squadmates, show intercept modal
        if (availableSquadmates.length > 0) {
            const triggerUserId = charUserId;
            const triggerRoles = charData.roles || charData.metadata?.role_labels || ["DPS"];
            setSquadIntercept({
                triggerCharId: charData.characterId || charData.id,
                triggerCharName: charData.name,
                triggerCharClassName: charData.className || charData.metadata?.class_name || "Unknown",
                triggerCharRoles: triggerRoles,
                triggerCharUserId: triggerUserId,
                squadInfo: charSquadInfo,
                targetIndex,
                availableSquadmates
            });
            // Initialize selections with ALL squad members (trigger + squadmates)
            const initialSelections: Record<string, { charId: string; roleLabel: string }> = {
                [triggerUserId]: { charId: charData.characterId || charData.id, roleLabel: triggerRoles[0] || "DPS" }
            };
            availableSquadmates.forEach(sm => {
                if (!initialSelections[sm.userId]) {
                    initialSelections[sm.userId] = { charId: sm.id, roleLabel: sm.roles[0] || "DPS" };
                }
            });
            setSquadSelections(initialSelections);
            setAllowSquadSplit(false); // Reset split mode
            return; // Don't proceed with normal drop - wait for modal
        }
    }

    // Determine available roles
    const availableRoles = charData.roles || charData.metadata?.role_labels || ["DPS"];

    // If only one role, assign immediately
    if (availableRoles.length === 1) {
        const roleLabel = availableRoles[0];

        const existingIdx = next.findIndex(a => Number(a.metadata?.seat_index) === targetIndex);
        if (existingIdx !== -1) { recordRemovals([next[existingIdx]], 'replaced'); next.splice(existingIdx, 1); }
        
        next.push({
            character_id: charData.characterId || charData.id,
            character_name: charData.name,
            character_class_name: charData.className || charData.metadata?.class_name,
            role_label: roleLabel,
            is_required: true,
            metadata: { seat_index: targetIndex, ...(charData.isPug ? { is_pug: true } : {}) },
            character_user_id: charData.userId || charData.ownerId,
            ...crossGuildFields(charData)
        });

        setActiveAssignments(next);
        setIsDirty(true);
    } else {
        // Open the selection modal for multiple roles
        setRoleSelection({ candidate: charData, targetIndex });
    }
};

const confirmAssignment = (roleLabel: string) => {
    if (!roleSelection) return;
    const { candidate, targetIndex } = roleSelection;
    const next = [...activeAssignments];

    const existingIdx = next.findIndex(a => Number(a.metadata?.seat_index) === targetIndex);
    if (existingIdx !== -1) { recordRemovals([next[existingIdx]], 'replaced'); next.splice(existingIdx, 1); }
    
    next.push({
        character_id: candidate.characterId || candidate.id,
        character_name: candidate.name,
        character_class_name: candidate.className || candidate.metadata?.class_name,
        role_label: roleLabel,
        is_required: true,
        metadata: { seat_index: targetIndex, ...(candidate.isPug ? { is_pug: true } : {}) },
        character_user_id: candidate.userId || candidate.ownerId,
        ...crossGuildFields(candidate)
    });

    setActiveAssignments(next);
    setIsDirty(true);
    setRoleSelection(null);
};

// Handle seating just the single character from squad intercept (no longer used - kept for compatibility)
const handleSeatSingle = () => {
    if (!squadIntercept) return;
    setSquadIntercept(null);
    setSquadSelections({});
    setAllowSquadSplit(false);
  };

  // Handle seating squad from intercept - uses squadSelections state
  const handleSeatSquad = () => {
    if (!squadIntercept) return;
    const { targetIndex } = squadIntercept;
    
    // Get all selected characters from squadSelections
    const selections = Object.values(squadSelections).filter(s => s.charId);
    if (selections.length === 0) return;
    
    const sourceRoster = candidates.length > 0 ? candidates : (guildRosterMap[activeRaid.guild_id] || []);
    const size = Number(activeRaid?.metadata?.size) || Number(activeRaid?.size) || 10;
    const next = [...activeAssignments];
    
    // Find empty slots, excluding targetIndex (first char will use that)
    const emptySlots: number[] = [];
    for (let i = 0; i < size; i++) {
        if (i !== targetIndex && !next.some(a => Number(a.metadata?.seat_index) === i)) {
            emptySlots.push(i);
        }
    }
    
    // Seat each selected character
    let emptyIdx = 0;
    selections.forEach((sel, idx) => {
        // First character goes to targetIndex, rest to empty slots
        const seatIndex = idx === 0 ? targetIndex : emptySlots[emptyIdx++];
        if (seatIndex === undefined || seatIndex >= size) return; // No more slots
        
        // Find character data
        const charData = sourceRoster.find((c: any) => c.characterId === sel.charId || c.id === sel.charId);
        if (!charData) return;

        const existingIdx = next.findIndex(a => Number(a.metadata?.seat_index) === seatIndex);
        if (existingIdx !== -1) { recordRemovals([next[existingIdx]], 'replaced'); next.splice(existingIdx, 1); }
        
        next.push({
            character_id: charData.characterId || charData.id,
            character_name: charData.name,
            character_class_name: charData.className || charData.metadata?.class_name,
            role_label: sel.roleLabel,
            is_required: true,
            metadata: { seat_index: seatIndex, ...(charData.isPug ? { is_pug: true } : {}) },
            character_user_id: charData.userId || charData.ownerId,
            ...crossGuildFields(charData)
        });
    });
    
    setActiveAssignments(next);
    setIsDirty(true);
    setSquadIntercept(null);
    setSquadSelections({});
    setAllowSquadSplit(false);
  };

  const handleUnassign = (slotIndex: number, reasonCode: string = 'unspecified') => {
    // Find the assignment being removed
    const removedAssignment = activeAssignments.find(a => Number(a.metadata?.seat_index) === slotIndex);
    if (!removedAssignment) {
        // No assignment at this slot, nothing to do
        setActiveAssignments(activeAssignments.filter(a => Number(a.metadata?.seat_index) !== slotIndex));
        setIsDirty(true);
        return;
    }

    // Check if this character's user is in a squad
    const removedUserId = removedAssignment.character_user_id;
    const squadInfo = removedUserId ? squadLookup[removedUserId] : null;

    if (squadInfo && squadInfo.squadSize > 1) {
        // Find all squad member user IDs
        const squadUserIds = new Set(squadInfo.memberUserIds);

        // Remove ALL assignments where the character's user is in this squad
        const removedRows = activeAssignments.filter(a => {
            const userId = a.character_user_id;
            return userId && squadUserIds.has(userId);
        });
        const next = activeAssignments.filter(a => {
            const userId = a.character_user_id;
            return !userId || !squadUserIds.has(userId);
        });

        recordRemovals(removedRows, reasonCode);
        setActiveAssignments(next);
        setIsDirty(true);
    } else {
        // No squad - just remove the single assignment
        const next = activeAssignments.filter(a => Number(a.metadata?.seat_index) !== slotIndex);
        recordRemovals([removedAssignment], reasonCode);
        setActiveAssignments(next);
        setIsDirty(true);
    }
};

  // Drop a player onto the true bench (backup). Single character; no squad auto-pull,
  // no role modal — the bench is a loose backup list. Promotion later assigns a seat.
  const handleBenchDrop = (origin: any) => {
    if (origin.type === 'benchseat') return; // already on the bench
    const next = [...activeAssignments];
    if (origin.type === 'slot') {
        // Demote a seated player to the bench.
        const idx = next.findIndex(a => Number(a.metadata?.seat_index) === origin.index);
        if (idx === -1) return;
        const meta = { ...(next[idx].metadata || {}) };
        delete meta.seat_index;
        meta.is_bench = true;
        next[idx] = { ...next[idx], is_required: false, metadata: meta };
        setActiveAssignments(next);
        setIsDirty(true);
        return;
    }
    // origin.type === 'bench' (available pool) -> add a new bench assignment.
    if (next.some(a => a.character_id === origin.id)) return; // already assigned somewhere
    let charData = candidates.find(c => c.characterId === origin.id) ||
                   guildRosterMap[activeRaid.guild_id]?.find((c: any) => c.characterId === origin.id);
    if (!charData) {
        charData = { id: origin.id, characterId: origin.id, name: "External", userId: "unknown", metadata: { class_name: "Unknown", role_labels: ["DPS"] } };
    }
    const availableRoles = charData.roles || charData.metadata?.role_labels || ["DPS"];
    next.push({
        character_id: charData.characterId || charData.id,
        character_name: charData.name,
        character_class_name: charData.className || charData.metadata?.class_name,
        role_label: availableRoles[0],
        is_required: false,
        metadata: { is_bench: true, ...(charData.isPug ? { is_pug: true } : {}) },
        character_user_id: charData.userId || charData.ownerId,
        ...crossGuildFields(charData)
    });
    setActiveAssignments(next);
    setIsDirty(true);
  };

  // Remove a player from the bench entirely (returns them to the available pool).
  const handleBenchRemove = (id: string, reasonCode: string = 'unspecified') => {
    const removedRows = activeAssignments.filter(a => {
        const matches = (a.id && a.id === id) || a.character_id === id;
        return matches && a.metadata?.is_bench;
    });
    recordRemovals(removedRows, reasonCode);
    setActiveAssignments(activeAssignments.filter(a => {
        const matches = (a.id && a.id === id) || a.character_id === id;
        return !(matches && a.metadata?.is_bench);
    }));
    setIsDirty(true);
  };

  // --- REVISED AUTO-FILL LOGIC (Suggestions) ---
  // Always shows top 3 candidates per role (Tank / Healer / Melee DPS / Ranged DPS).
  // Preserves ALL constraint filters from ROSTER_POPULATION.md §2.4:
  //   seated · alt-of-seated (bilocation) · isInterested · isBusy · isLockedOut ·
  //   isConfirmed · isCharacterAvailable.
  // Adds ONE new rule: dedupe by ownerId across the full pool, so at most one
  // character per player is ever surfaced — preventing the host from adding two
  // alts of the same user and bilocating them on save.

  function getCompTargets(size: number): Record<string, number> {
    let tank = 2, healer = 2;
    if (size >= 25) { tank = 3; healer = 5; }
    if (size >= 40) { tank = 3; healer = 8; }
    const dps = Math.max(0, size - tank - healer);
    return { "Tank": tank, "Healer": healer, "DPS": dps };
}

async function handleAutoFill() {
  if (!activeRaid) return;

  // 1. Compute "needed" counts (display only — no longer gates suggestion display)
  const size = Number(activeRaid.metadata?.size) || Number(activeRaid.size) || 10;
  const targets = getCompTargets(size);
  const currentCounts: Record<string, number> = { "Tank": 0, "Healer": 0, "Melee DPS": 0, "Ranged DPS": 0 };

  activeAssignments.forEach(a => {
      const r = a.role_label || "";
      if (r === "Tank") currentCounts["Tank"]++;
      else if (r === "Healer") currentCounts["Healer"]++;
      else if (r === "Melee DPS") currentCounts["Melee DPS"]++;
      else if (r === "Ranged DPS") currentCounts["Ranged DPS"]++;
  });

  // Split DPS target evenly between Melee/Ranged for display purposes
  const dpsHalf = Math.ceil(targets.DPS / 2);
  const missing: Record<string, number> = {
      "Tank":       Math.max(0, targets.Tank   - currentCounts["Tank"]),
      "Healer":     Math.max(0, targets.Healer - currentCounts["Healer"]),
      "Melee DPS":  Math.max(0, dpsHalf        - currentCounts["Melee DPS"]),
      "Ranged DPS": Math.max(0, dpsHalf        - currentCounts["Ranged DPS"]),
  };

  // 2. Fetch Candidates (unchanged)
  const raidWindows = sessionWindowsForRaid(activeRaid, userTz);

  let candidates: (HeuristicCharacter & { wclScore?: number; isInterested?: boolean; isLockedOut?: boolean; isBusy?: boolean })[] = [];
  try {
      const res = await fetch(`/raids/${activeRaid.id}/candidates`, { credentials: 'include' });
      const poolData = await res.json();
      candidates = poolData.map((m: any) => ({
          id: m.characterId,
          name: m.name,
          ownerId: m.userId,
          guildId: m.guildId,
          className: m.metadata?.class_name || "Unknown",
          roles: m.metadata?.role_labels || [m.metadata?.primary_role_label || m.metadata?.primary_role || "DPS"],
          availabilityBits: m.userAvailability,
          ilvl: m.ilvl,
          wclScore: m.metadata?.wcl_perf?.score,
          characterId: m.characterId,
          userId: m.userId,
          userAvailability: m.userAvailability,
          metadata: m.metadata,
          options: m.options || [],
          isInterested: m.isInterested,
          isLockedOut: m.isLockedOut,
          isBusy: m.isBusy,
          isConfirmed: m.isConfirmed,
          isCrossGuild: m.isCrossGuild || false,
          sourceGuildName: m.sourceGuildName || null,
          sourceGuildTag: m.sourceGuildTag || null
      }));
  } catch { return; }

  // 3. Filter — preserves all existing constraints exactly
  const seatedIds = new Set(activeAssignments.map(a => a.character_id));
  const lockedUsers = new Set<string>();
  activeAssignments.forEach(a => {
      const c = candidates.find(x => x.id === a.character_id);
      if (c?.ownerId) lockedUsers.add(c.ownerId);
  });

  const validPool = candidates.filter(c => {
      if (seatedIds.has(c.id)) return false;
      if (c.ownerId && lockedUsers.has(c.ownerId)) return false;                     // alt of seated user
      if (c.isInterested === false) return false;                                     // uninterested
      if (c.isBusy === true) return false;                                            // busy in an overlapping raid
      if (c.isLockedOut === true) return false;                                       // instance lockout
      if (!ignoreAvailability && !(c as any).isConfirmed) return false;               // unconfirmed availability
      if (!ignoreAvailability && !isCharacterAvailable(c, raidWindows)) return false; // time overlap
      return true;
  });

  // Sort: parse desc, then host-guild-first tiebreak (per ROSTER_POPULATION.md §2.4)
  validPool.sort((a, b) => {
      const aScore = a.wclScore || 0;
      const bScore = b.wclScore || 0;
      if (aScore !== bScore) return bScore - aScore;
      return (a.guildId === activeRaid.guild_id ? -1 : 1);
  });

  // 4. Dedupe by ownerId — keep each user's best character only.
  //    validPool is already sorted, so first occurrence == highest-ranked.
  const seenOwners = new Set<string>();
  const uniquePool: typeof validPool = [];
  for (const c of validPool) {
      const key = c.ownerId || `_char_${c.id}`;
      if (seenOwners.has(key)) continue;
      seenOwners.add(key);
      uniquePool.push(c);
  }

  // 5. Always build all 4 role groups — top 3 each.
  //    A multi-role character (e.g., Warrior ["Tank","Melee DPS"]) may appear in
  //    both eligible groups — but handleAddCandidate's on-click filter (line ~1387)
  //    removes the character AND all their alts from every group the moment they're
  //    seated, so no double-seating path exists.
  const ROLE_ORDER: Array<{ key: string; match: (r: string) => boolean }> = [
      { key: "Tank",       match: r => r === "Tank" },
      { key: "Healer",     match: r => r === "Healer" },
      { key: "Melee DPS",  match: r => r === "Melee DPS" },
      { key: "Ranged DPS", match: r => r === "Ranged DPS" },
  ];

  const resultGroups: SuggestionGroup[] = ROLE_ORDER.map(({ key, match }) => ({
      role: key,
      needed: missing[key],
      candidates: uniquePool.filter(c => c.roles.some(match)).slice(0, 3),
  }));

  setSuggestions(resultGroups);
  setSuggestionsOpen(true);
}

  const handleAddCandidate = (candidate: HeuristicCharacter, role: string) => {
      // Find first empty slot
      const size = Number(activeRaid.metadata?.size) || Number(activeRaid.size) || 10;
      let targetIdx = -1;
      
      // Try to find a logically empty slot at the end, or holes
      for (let i=0; i<size; i++) {
          if (!activeAssignments.some(a => Number(a.metadata?.seat_index) === i)) {
              targetIdx = i;
              break;
          }
      }

      if (targetIdx === -1) {
          alert("No empty slots available on the board.");
          return;
      }

      // Add to roster.
      // We additionally carry cross-guild context + user id on the local assignment
      // so the per-seat state badge can render "pending save" between drop and save.
      // handleSaveRoster's PUT payload only reads character_id / role_label / is_required / metadata,
      // so the extra fields are inert on the save path and preserve existing save semantics.
      const cand: any = candidate as any;
      const newAssign = {
        character_id: candidate.id,
        character_name: candidate.name,
        character_class_name: candidate.className,
        role_label: role === "DPS" ? (candidate.roles.find(r => r.includes("DPS")) || "Melee DPS") : role,
        is_required: true,
        metadata: { seat_index: targetIdx, ...(cand.isPug ? { is_pug: true } : {}) },
        guild_raid_id: activeRaid.id,
        character_user_id: cand.ownerId || null,
        isCrossGuild: !!cand.isCrossGuild,
        sourceGuildName: cand.sourceGuildName || null,
        sourceGuildTag: cand.sourceGuildTag || null
    };

      setActiveAssignments(prev => [...prev, newAssign]);
      setIsDirty(true);

      // Cross-guild drop: the invite panel picks this up straight away, and sending
      // the DM saves the roster itself, so point at the DM rather than at Save.
      if (cand.isCrossGuild) {
          const from = cand.sourceGuildName || 'another guild';
          showToast(`Added ${candidate.name} from ${from}. Send them a Discord DM to confirm.`, 'info');
      }

      // Remove from suggestions locally to prevent double click
      setSuggestions(prev => prev.map(g => ({
          ...g,
          candidates: g.candidates.filter(c => c.id !== candidate.id && c.ownerId !== candidate.ownerId)
      })));
  };

  async function handleDeleteRaid(raidId: string) {
    const isConfirmed = await confirm({
      title: "Delete Operation?",
      description: "This will permanently remove the raid and all roster assignments. This action cannot be undone.",
      confirmText: "Delete Raid",
      variant: "danger",
    });

    if (!isConfirmed) return;

    try {
      const res = await fetch(`/raids/${encodeURIComponent(String(raidId))}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) { alert("Failed to delete raid."); return; }
      fetchRaids(); // <--- REFRESH WITHOUT RELOAD
      setMode("list");
    } catch (err) { alert("Network error."); }
  }

  // Raider removes THEMSELVES from the active raid. The server enforces both that
  // only the caller's own characters are dropped and that the raid hasn't started.
  async function handleLeaveRaid() {
    if (!activeRaidId) return;
    const isConfirmed = await confirm({
      title: "Leave this raid?",
      description: "You'll be removed from the roster. If your guild's Discord bot is set up, your raid leaders will be notified.",
      confirmText: "Leave Raid",
      variant: "danger",
    });
    if (!isConfirmed) return;

    try {
      const res = await fetch(`/raids/${encodeURIComponent(activeRaidId)}/leave`, { method: "POST", credentials: "include" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.error === "raid_already_started") showToast("This raid has already started — you can no longer remove yourself.", "error");
        else if (data.error === "not_assigned") showToast("You're not on this raid's roster.", "error");
        else showToast("Failed to leave the raid.", "error");
        return;
      }
      showToast("You've left the raid.", "success");
      fetchRaids();
      openRaidDetail(activeRaidId); // refresh detail so the roster reflects the change
    } catch {
      showToast("Network error — please try again.", "error");
    }
  }

  // ... (handleCreate remains same) ...
  async function handleCreate(values: CreateRaidFormValues) {
    setCreating(true);
    setError(null);
    try {
      let assignments: any[] = [];
      const roster = values.metadata && (values.metadata as any).roster;
      if (roster && Array.isArray((roster as any).slots) && Array.isArray((roster as any).roles)) {
        const slots = (roster as any).slots as (string | null)[];
        const roles = (roster as any).roles as (string | null)[];
        assignments = slots.map((characterId, index) => {
            if (!characterId) return null;
            const roleLabel = roles[index] || null;
            return { characterId, roleLabel, isRequired: true, metadata: { seat_index: index, source: "manual" } };
        }).filter(Boolean) as any[];
        // True bench: seat-less assignments flagged is_bench (mirrors PUG seat-less pattern;
        // counts for lockout/bilocation/soft-reserve, excluded from seated grid by seat_index check).
        const bench = Array.isArray((roster as any).bench) ? (roster as any).bench as string[] : [];
        const benchRoles = Array.isArray((roster as any).benchRoles) ? (roster as any).benchRoles as (string | null)[] : [];
        bench.forEach((characterId, bIdx) => {
            if (!characterId) return;
            assignments.push({ characterId, roleLabel: benchRoles[bIdx] || null, isRequired: false, metadata: { is_bench: true, bench_index: bIdx, source: "manual" } });
        });
    }

      if (values.isRecurring && values.recurringPatterns) {
        const scheduleConfig: any = { patterns: {} };
        Object.entries(values.recurringPatterns).forEach(([day, pattern]) => {
            const today = new Date().toISOString().slice(0, 10);
            const s = new Date(`${today}T${(pattern as any).start}`);
            const e = new Date(`${today}T${(pattern as any).end}`);
            if (e < s) e.setDate(e.getDate() + 1);
            const duration = Math.round((e.getTime() - s.getTime()) / 60000);
            scheduleConfig.patterns[day] = { time: (pattern as any).start, duration: duration };
        });

        const createRes = await fetch(`/guilds/${encodeURIComponent(values.guildId)}/raid-series`, {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                gameId: values.gameId, raidOptionId: values.raidOptionId ?? null, name: values.raidLabel,
                requiredNightsPerWeek: Object.keys(scheduleConfig.patterns).length, scheduleConfig, assignments,
                metadata: { ...values.metadata, size: values.size },
                // Recurring raids chain the same way one-off raids do. Without this the
                // generated raids carry no instance rows and lock nobody out.
                chainedRaidOptionIds: values.chainedRaidOptionIds || undefined,
                // Server expands the recurring pattern into UTC `starts_at` instants — it needs
                // the creator's IANA TZ to interpret "Mon 20:30" as 20:30 in their wall clock.
                // See TIMEZONE_MIGRATION_PLAN.md §4c.
                creatorTz: userTz,
            }),
        });
        if (!createRes.ok) throw new Error("Series create failed");

      } else {
        const createRes = await fetch(`/guilds/${encodeURIComponent(values.guildId)}/raids`, {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                gameId: values.gameId, raidOptionId: values.raidOptionId ?? null, name: values.raidLabel,
                sessions: values.sessions || [], size: values.size, raidLeaderCharacterId: values.raidLeaderCharacterId,
                lootMasterCharacterId: values.lootMasterCharacterId, metadata: { ...values.metadata, size: values.size },
                chainedRaidOptionIds: values.chainedRaidOptionIds || undefined,
                is_published: values.publish === true,
            }),
        });
        if (!createRes.ok) throw new Error("Create failed");
        const createdRaid = await createRes.json();
        
        if (assignments.length > 0) {
            await fetch(`/raids/${encodeURIComponent(String(createdRaid.id))}/assignments`, {
                method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ assignments, raidLeaderCharacterId: values.raidLeaderCharacterId, lootMasterCharacterId: values.lootMasterCharacterId }),
            });
        }

        // Create soft reserve config for primary raid (chain reserves are counted across all raids)
        if (values.softReserveEnabled) {
            // SR config only on primary raid — backend counts across chain
            await fetch(`/raids/${encodeURIComponent(String(createdRaid.id))}/soft-reserve-config`, {
                method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ maxReservesPerPlayer: values.maxSoftReserves || 2, allowDuplicateItems: true }),
            });
        }
      }
      setMode("list");
      fetchRaids(); // <--- REFRESH WITHOUT RELOAD
    } catch (e: any) { setError(e.message); } finally { setCreating(false); }
  }

// Update openRaidDetail to load existing leaders
async function openRaidDetail(raidId: string) {
    setActiveRaidId(raidId); setActiveRaid(null); setActiveAssignments([]); setDetailError(null); setSrStatus(null);
    setDetailLoading(true); setMode("detail"); setIsDirty(false); setCanManage(false);
    setPendingRemovals({}); setRemovalPrompt(null);
    
    // [NEW] Reset leadership state
    setEditRaidLeader("");
    setEditLootMaster("");

    try {
        const res = await fetch(`/raids/${encodeURIComponent(raidId)}/detail`, { credentials: "include" });
        if (!res.ok) throw new Error("Detail failed");
        const data = await res.json();
        
        setActiveRaid(data.raid || null);
        setActiveAssignments(Array.isArray(data.assignments) ? data.assignments : []);
        setCanManage(data.canManage === true); 
        setCrossGuildNotices(Array.isArray(data.crossGuildNotices) ? data.crossGuildNotices : []); 
        
        // [NEW] Initialize leadership from DB
        if (data.raid) {
            setEditRaidLeader(data.raid.raid_leader?.character_id || "");
            setEditLootMaster(data.raid.loot_master?.character_id || "");
        }

        // Fetch candidates for bench status
        const candRes = await fetch(`/raids/${encodeURIComponent(raidId)}/candidates`, { credentials: "include" });
        if (candRes.ok) setCandidates(await candRes.json());

        // Roster change timeline (non-blocking)
        setRosterEventLog(null);
        fetch(`/raids/${encodeURIComponent(raidId)}/roster-events`, { credentials: "include" })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d && Array.isArray(d.events)) setRosterEventLog(d); })
          .catch(() => {});

        // Fetch soft reserve status
        setSrStatus(null);
        fetch(`/raids/${encodeURIComponent(raidId)}/reserves`, { credentials: "include" })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.config) {
              const myCharIds = new Set(
                (data.softReserves || [])
                  .filter((sr: any) => characters.some((c: any) => c.id === sr.character_id))
                  .map((sr: any) => sr.id)
              );
              const myItems = (data.softReserves || [])
                .filter((sr: any) => characters.some((c: any) => c.id === sr.character_id))
                .map((sr: any) => sr.item_name);
              setSrStatus({
                enabled: true,
                myCount: myItems.length,
                max: data.config.max_reserves_per_player,
                locked: data.config.effectivelyLocked || false,
                myItems,
              });
            }
          })
          .catch(() => {});

        // Fetch game fields + composition tags for spec resolution & buff coverage
        if (data.raid?.game_id) {
          fetch(`/games/${encodeURIComponent(data.raid.game_id)}/fields`, { credentials: "include" })
            .then(r => r.ok ? r.json() : [])
            .then((fields: FieldGroup[]) => setCurrentGameFields(fields))
            .catch(() => setCurrentGameFields(null));
          fetch(`/games/${encodeURIComponent(data.raid.game_id)}/composition-tags`, { credentials: "include" })
            .then(r => r.ok ? r.json() : [])
            .then((tags: CompositionTag[]) => setCompositionTags(tags))
            .catch(() => setCompositionTags([]));
        }

    } catch (e: any) { setDetailError(e.message); } finally { setDetailLoading(false); }
}

// Update handleSaveRoster to send leadership IDs.
// Returns whether the roster actually reached the server: the cross-guild DM flow
// saves first (notice rows are created by this endpoint) and must not fire on a
// roster the server never accepted.
async function handleSaveRoster(): Promise<boolean> {
    if (!activeRaidId) return false;
    setIsSaving(true);
    const payload = activeAssignments.map(a => ({ 
        characterId: a.character_id, 
        roleLabel: a.role_label, 
        isRequired: !!a.is_required, 
        metadata: a.metadata 
    }));
    
    try {
        const saveRes = await fetch(`/raids/${encodeURIComponent(activeRaidId)}/assignments`, { 
            method: "PUT", 
            credentials: "include", 
            headers: { "Content-Type": "application/json" }, 
            body: JSON.stringify({
                assignments: payload,
                // Reasons for anyone dropped since the last save; the server diffs
                // the roster itself and logs these against the removed characters.
                removals: Object.values(pendingRemovals).map(r => ({ characterId: r.characterId, reasonCode: r.reasonCode })),
                raidLeaderCharacterId: editRaidLeader || null,
                lootMasterCharacterId: editLootMaster || null
            })
        });
        if (!saveRes.ok) {
            showToast("Failed to save assignments.", "error");
            return false;
        }
        // Capture cross-guild notices from the save response so the banner refreshes
        // immediately. openRaidDetail below also re-syncs, but runs asynchronously.
        try {
            const saveData = await saveRes.json();
            if (Array.isArray(saveData?.crossGuildNotices)) {
                setCrossGuildNotices(saveData.crossGuildNotices);
            }
        } catch (_) { /* ignore — body may be empty on older servers */ }
        setIsDirty(false);
        setPendingRemovals({});
        setRemovalPrompt(null);
        // Awaited so a caller that acts on the saved roster (the cross-guild DM flow)
        // can't have this refresh land on top of its own result afterwards.
        await openRaidDetail(activeRaidId); // Reload to confirm changes
        return true;
    } catch (err) { alert("Failed to save assignments."); return false; } finally { setIsSaving(false); }
}

// --- Edit Raid Functions ---
async function openEditModal(raidId: string) {
    setEditError(null);
    setShowForceConfirm(false);
    setForceConfirmData(null);
    setConfirmRemoveSr(false);

    try {
        const res = await fetch(`/raids/${encodeURIComponent(raidId)}/edit-info`, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to load raid info");
        const data = await res.json();
        
        setEditInfo(data);
        setEditName(data.name || "");
        setEditRaidOptionId(data.raidOptionId);
        setEditChainedRaidOptionIds(Array.isArray(data.chainedRaidOptionIds) ? data.chainedRaidOptionIds : []);

        // Convert sessions to editable format (local picker parts from UTC instants)
        const sessionData = (data.sessions || []).map((s: any) => {
            const raw = s.starts_at;
            const startIsoRaw =
              typeof raw === "string" ? raw : raw instanceof Date ? raw.toISOString() : new Date(raw).toISOString();
            const anchorUtc = DateTime.fromISO(startIsoRaw, { zone: "utc" });
            const startIso = anchorUtc.isValid ? anchorUtc.toISO() || startIsoRaw : startIsoRaw;
            const duration = s.duration_minutes || 180;
            const endIso = DateTime.fromISO(startIso, { zone: "utc" }).plus({ minutes: duration }).toISO() || startIso;
            const sp = utcToPickerParts(startIso, userTz);
            const ep = utcToPickerParts(endIso, userTz);
            return { date: sp.date, startTime: sp.time, endTime: ep.time };
        });
        const finalSessions = sessionData.length > 0 ? sessionData : [{ date: '', startTime: '20:00', endTime: '23:00' }];
        setEditSessions(finalSessions);
        setOriginalEditSessions(JSON.parse(JSON.stringify(finalSessions)));
        
        setShowEditModal(true);
    } catch (err: any) {
        alert(err.message || "Failed to open edit modal");
    }
}

function hasScheduleChanged(): boolean {
    if (editSessions.length !== originalEditSessions.length) return true;
    return editSessions.some((s, i) => {
        const o = originalEditSessions[i];
        return s.date !== o.date || s.startTime !== o.startTime || s.endTime !== o.endTime;
    });
}

async function handleSaveEdit(forceEdit = false) {
    if (!editInfo) return;
    
    // If schedule changed and we haven't confirmed yet, show confirmation
    if (!forceEdit && hasScheduleChanged()) {
        setShowScheduleConfirm(true);
        return;
    }
    
    setEditSaving(true);
    setEditError(null);
    setShowScheduleConfirm(false);
    
    try {
        // Convert sessions to API format
        const sessions = editSessions.filter(s => s.date).map(s => {
            const startIso = pickerPartsToUtc(s.date, s.startTime, userTz);
            const startDt = DateTime.fromISO(startIso, { zone: "utc" });
            let endDt = DateTime.fromISO(pickerPartsToUtc(s.date, s.endTime, userTz), { zone: "utc" });
            if (!startDt.isValid || !endDt.isValid) {
              throw new Error("Invalid session date or time");
            }
            if (endDt <= startDt) endDt = endDt.plus({ days: 1 });
            const durationMinutes = Math.max(1, Math.round(endDt.diff(startDt, "minutes").minutes));
            return { startsAt: startDt.toISO() || startIso, durationMinutes };
        });
        
        const scheduleChanged = hasScheduleChanged();
        
        const res = await fetch(`/raids/${encodeURIComponent(editInfo.id)}`, {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                name: editName,
                raidOptionId: editRaidOptionId,
                chainedRaidOptionIds: editChainedRaidOptionIds,
                sessions: scheduleChanged && sessions.length > 0 ? sessions : undefined,
                forceEdit: true
            })
        });
        
        const data = await res.json();
        
        if (!res.ok) {
            throw new Error(data.details || data.message || data.error || "Failed to save");
        }
        
        // Success — show warnings inline if any players were removed
        if (data.warnings && data.warnings.length > 0) {
            setEditError(null);
            // Brief toast-style: show in the edit error area before closing
            const warnMsg = data.warnings.join(' ');
            setTimeout(() => alert(warnMsg), 300);
        }
        
        setShowEditModal(false);
        fetchRaids();
        if (activeRaidId) openRaidDetail(activeRaidId);
        
    } catch (err: any) {
        setEditError(err.message || "Failed to save changes");
    } finally {
        setEditSaving(false);
    }
}

async function handleRemoveSoftReserve() {
    if (!editInfo) return;
    setSrRemoving(true);
    setEditError(null);
    try {
        const res = await fetch(`/raids/${encodeURIComponent(editInfo.id)}/soft-reserve-config`, {
            method: "DELETE",
            credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.error || "Failed to remove soft reserves");
        }
        // Reflect the change locally so the section updates without reopening
        setEditInfo((prev: any) => prev ? { ...prev, softReserveEnabled: false, softReserveCount: 0 } : prev);
        setConfirmRemoveSr(false);
        fetchRaids();
        if (activeRaidId) openRaidDetail(activeRaidId);
    } catch (err: any) {
        setEditError(err.message || "Failed to remove soft reserves");
    } finally {
        setSrRemoving(false);
    }
}

function handleEditSessionChange(index: number, field: string, value: string) {
    setEditSessions(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
    });
}

  const bumpEditSessionStart = useCallback(
    (index: number, deltaMinutes: number) => {
      setEditSessions((prev) => {
        const updated = [...prev];
        const cur = updated[index];
        if (!cur.date) {
          updated[index] = { ...cur, startTime: adjustTime(cur.startTime, deltaMinutes) };
          return updated;
        }
        const sh = shiftWallClock(cur.date, cur.startTime, deltaMinutes, userTz);
        updated[index] = { ...cur, date: sh.date, startTime: sh.time };
        return updated;
      });
    },
    [userTz]
  );

  const bumpEditSessionEnd = useCallback(
    (index: number, deltaMinutes: number) => {
      setEditSessions((prev) => {
        const updated = [...prev];
        const cur = updated[index];
        if (!cur.date) {
          updated[index] = { ...cur, endTime: adjustTime(cur.endTime, deltaMinutes) };
          return updated;
        }
        const sh = shiftWallClock(cur.date, cur.endTime, deltaMinutes, userTz);
        updated[index] = { ...cur, endTime: sh.time };
        return updated;
      });
    },
    [userTz]
  );

  function handleOpenLootCouncilFromDetail() {
    if (!activeRaidId || !activeRaid) return;
    setLootCouncilReturnMode("detail");
    setLootCouncilRaidId(activeRaidId);
    setLootCouncilGuildId(activeRaid.guild_id);
    setLootCouncilRaidName(activeRaid.name || activeRaid.raid_name || "Raid");
    setMode("loot-council");
  }

  // --- RENDER ---
  if (mode === "arc" && meLoaded && !adminViewGuildId) {
    return themed(
      <ArcPage
        guilds={guilds.map((g: any) => ({ id: g.id, name: g.name, gameId: g.gameId, logoUrl: g.logoUrl }))}
        onCancel={() => setMode("list")}
        onCommitSuccess={() => { setMode("list"); if (typeof window.refreshPlannerRaids === 'function') window.refreshPlannerRaids(); }}
      />, true
    );
}

if (mode === "create" && meLoaded && !adminViewGuildId) {
    return themed(
      <CreateRaidPage
          guilds={guilds}
          characters={characters}
          accountAvailability={accountAvailability}
          onCancel={() => setMode("list")}
          onSubmit={handleCreate}
          submitting={creating}
          error={error}
          me={me}
        />
      );
  }

  if (mode === "soft-reserve" && activeRaidId && activeRaid) {
    // Build myCharacters from the user's assigned characters in this raid
    const myCharsForSR = candidates
      .filter((c: any) => c.userId === me?.id)
      .map((c: any) => ({ id: c.characterId, name: c.characterName || c.name, class_name: c.className || c.class_name || c.metadata?.class_name || null }));
    // Fallback: use characters prop filtered to this guild
    const srChars = myCharsForSR.length > 0 ? myCharsForSR : characters
      .filter((c: any) => c.guildId === activeRaid.guild_id)
      .map((c: any) => ({ id: c.id, name: c.name, class_name: c.className || c.class_name || c.metadata?.class_name || null }));

    // Find the character enrolled/assigned to this raid's roster
    const myAssignment = activeAssignments.find((a: any) => {
      const charOwner = candidates.find((c: any) => c.characterId === a.character_id);
      return charOwner && charOwner.userId === me?.id;
    });
    const enrolledCharId = myAssignment?.character_id || "";

    return themed(
      <SoftReservePage
        raidId={activeRaidId}
        raidInfo={{
          id: activeRaidId,
          name: activeRaid.name,
          guild_id: activeRaid.guild_id,
          game_id: activeRaid.game_id,
          raid_option_id: activeRaid.raid_option_id,
          raidName: activeRaid.name,
          hostedByGuild: activeRaid.guild_name,
          sessions: activeRaid.sessions,
        }}
        canManage={canManage}
        myCharacters={srChars}
        enrolledCharacterId={enrolledCharId}
        onBack={() => setMode("detail")}
        />
    );
  }

  // The sheet works out the roster, the viewer and the boss list from the raid
  // id on its own, so it needs nothing else from the planner.
  if (mode === "assignments" && activeRaidId && activeRaid) {
    return themed(
      <AssignmentBoardPage
        raidId={activeRaidId}
        raidName={activeRaid.name}
        onBack={() => setMode("detail")}
      />
    );
  }

  if (mode === "loot-council" && lootCouncilRaidId && lootCouncilGuildId) {
    return themed(
      <PlannerLootCouncilPanel
        raidId={lootCouncilRaidId}
        guildId={lootCouncilGuildId}
        raidName={lootCouncilRaidName}
        onClose={() => {
          const ret = lootCouncilReturnMode;
          setLootCouncilRaidId(null);
          setLootCouncilGuildId(null);
          setMode(ret);
          if (ret === "detail" && activeRaidId) {
            void openRaidDetail(activeRaidId);
          }
          fetchRaids();
        }}
        showToast={showToast}
      />
    );
  }

  if (mode === "detail" && activeRaidId) {
    const raid = activeRaid;
    if (!raid) return null;
    const size = Number(raid.metadata?.size) || Number(raid.size) || 10;
    const rosterArray = new Array(size).fill(null);
    activeAssignments.forEach((a: any) => { 
        if (a.metadata?.seat_index === undefined || a.metadata?.seat_index === null) return; // Skip bench-only (e.g. PUG) assignments with no seat
        const idx = Number(a.metadata.seat_index); 
        if (idx >= 0 && idx < size) rosterArray[idx] = a; 
    });    const stats = { tank: 0, healer: 0, dps: 0 };
    activeAssignments.forEach(c => { const r = c.role_label?.toLowerCase()||''; if(r.includes('tank')) stats.tank++; else if(r.includes('healer')) stats.healer++; else stats.dps++; });

    // Self-leave gate: a player may remove their OWN tile (via its hover X) only before
    // the raid's earliest session starts. Per-tile ownership (assignmentUserId === me.id)
    // is checked at each seat. The server re-enforces both ownership and the cutoff.
    const earliestStartMs = (raid.sessions && raid.sessions.length)
        ? Math.min(...raid.sessions
            .map((s: any) => new Date(s.starts_at).getTime())
            .filter((n: number) => !Number.isNaN(n)))
        : null;
    const raidNotStarted = earliestStartMs == null || earliestStartMs > Date.now();

    const sessionChips =
      raid.sessions && Array.isArray(raid.sessions)
        ? raid.sessions.map((s: any) => {
            const raw = s.starts_at;
            const startIso =
              typeof raw === "string"
                ? raw
                : raw instanceof Date
                  ? raw.toISOString()
                  : new Date(raw).toISOString();
            const anchorUtc = DateTime.fromISO(startIso, { zone: "utc" });
            const isoForFmt = anchorUtc.isValid ? anchorUtc.toISO() || startIso : startIso;
            const t = formatRaidTime(isoForFmt, userTz);
            return `${t.date} @ ${t.time}`;
          })
        : [];

    // Use fetched candidates if available, otherwise fallback to guild roster
    const sourceRoster = candidates.length > 0 ? candidates : (guildRosterMap[raid.guild_id] || []);
    const assignedUserIds = new Set<string>();
    activeAssignments.forEach(a => {
        const char = sourceRoster.find((c: any) => c.characterId === a.character_id);
        if (char && char.userId) assignedUserIds.add(char.userId);
    });
    const raidWindows = sessionWindowsForRaid(raid, userTz);
    
    const bench = sourceRoster.filter((c: any) => {
        const isSeated = activeAssignments.some(a => a.character_id === c.characterId);
        return !isSeated;
    }).map((c: any) => {
        const isInterested = c.isInterested !== false; // Default true if undefined
        const isTimeAvailable = ignoreAvailability || isCharacterAvailable({ id: c.characterId, availabilityBits: c.userAvailability } as any, raidWindows);
        const isUserLocked = c.userId && assignedUserIds.has(c.userId);
        const isLockedOut = c.isLockedOut === true;
        const isBusy = c.isBusy === true; // User has another char in a raid at this time
        const isConfirmed = c.isConfirmed === true; // Explicit weekly confirmation required (no legacy/implicit default)
        
        let reason = null;
        if (isBusy) reason = "In Another Raid"; // Bilocation - highest priority
        else if (isLockedOut) reason = "Locked Out";
        else if (isUserLocked) reason = "Alt Signed";
        else if (!isInterested) reason = "Not Interested";
        else if (!isConfirmed) reason = "Not Confirmed";
        else if (!isTimeAvailable) reason = "Unavailable";

        return { ...c, isAvailable: isTimeAvailable && !isUserLocked && isInterested && !isLockedOut && !isBusy && isConfirmed, unavailableReason: reason };
    }).sort((a: any, b: any) => {
        if (a.isAvailable && !b.isAvailable) return -1;
        if (!a.isAvailable && b.isAvailable) return 1;
        if (a.unavailableReason === "Not Interested" && b.unavailableReason !== "Not Interested") return 1;
        if (a.unavailableReason !== "Not Interested" && b.unavailableReason === "Not Interested") return -1;
        return 0;
    });

    // Own-guild members stay in the Roster Pool (shown available + unavailable).
    // Released cross-guild players (opted in via "Open to Other Raid Teams" AND released
    // by their guild) go to a separate section below, and only when actually available.
    const ownBench = bench.filter((c: any) => !c.isCrossGuild);
    const crossGuildAvailable = bench.filter((c: any) => c.isCrossGuild && c.isAvailable);

    // Cross-guild seats dropped since the last save have no notice row in the DB yet.
    // Mirror them as pending notices so the invite panel and its Send DM button are
    // live the moment the player lands on the board — sending saves the roster first,
    // which is what creates the real rows. Keyed on user_id, same as the DB table.
    const seenNoticeUserIds = new Set(crossGuildNotices.map(n => n.user_id));
    const pendingCrossGuildNotices: CrossGuildNotice[] = [];
    for (const a of activeAssignments as any[]) {
        if (!a.isCrossGuild) continue;
        const uid = a.character_user_id || a.user_id || a.userId;
        if (!uid || seenNoticeUserIds.has(uid)) continue;
        seenNoticeUserIds.add(uid);
        pendingCrossGuildNotices.push({
            id: `pending:${uid}`,
            user_id: uid,
            character_id: a.character_id,
            source_guild_id: null,
            notified_at: null,
            notified_via: null,
            loot_info_sent: null,
            discord_invite_sent: null,
            response: null,
            response_at: null,
            created_at: new Date().toISOString(),
            character_name: a.character_name,
            class_name: a.character_class_name || null,
            // Owner display name and Discord link only come back with the saved row.
            user_display_name: '',
            discord_id: null,
            source_guild_name: a.sourceGuildName || null,
            source_guild_tag: a.sourceGuildTag || null,
            pending: true,
        });
    }
    const allCrossGuildNotices = [...crossGuildNotices, ...pendingCrossGuildNotices];

    return themed(
        <div className="max-w-[1700px] mx-auto pb-10 relative">
            <div className="relative mb-6 overflow-hidden rounded-3xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)]">
                {/* Background Raid Image */}
                {raid.raid_image_url && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                        <div
                            className="absolute inset-0"
                            style={{
                                maskImage: 'linear-gradient(to right, black 0%, black 20%, transparent 70%), linear-gradient(to bottom, black 0%, black 30%, transparent 80%)',
                                WebkitMaskImage: 'linear-gradient(to right, black 0%, black 20%, transparent 70%), linear-gradient(to bottom, black 0%, black 30%, transparent 80%)',
                                maskComposite: 'intersect',
                                WebkitMaskComposite: 'source-in' as any,
                            }}
                        >
                            <img src={raid.raid_image_url} className="w-full h-full object-cover opacity-30" alt="" />
                        </div>
                    </div>
                )}

                <div className="relative z-10 p-6">
                <div className="flex flex-col lg:flex-row gap-6 lg:items-stretch justify-between">
                        {/* Left: Info */}
                        <div className="space-y-4 flex-1">
                            {/* Guild Logo + Meta Badges */}
                            <div className="flex items-center gap-3">
                                {(() => {
                                    let logoUrl = raid.guild_logo_url || null;
                                    if (logoUrl && !logoUrl.startsWith("http")) logoUrl = `${API_URL}${logoUrl}`;
                                    return logoUrl ? (
                                        <img src={logoUrl} alt="" className="h-10 w-10 shrink-0 rounded-full border-2 border-[var(--c-line)] bg-[var(--c-sunken-2)] object-cover shadow-lg" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display='none'; }} />
                                    ) : (
                                        <div className="h-10 w-10 shrink-0 rounded-full bg-indigo-500/20 border-2 border-indigo-500/30 flex items-center justify-center text-sm text-indigo-700 dark:text-indigo-300 font-bold shadow-lg">
                                            {(raid.guild_name || "G").charAt(0).toUpperCase()}
                                        </div>
                                    );
                                })()}
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-bold text-[var(--c-ink-2)]">{raid.guild_name}</span>
                                    <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[var(--c-ink-3)]">
                                        {!raid.is_published && (
                                            <span className="px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300">Draft</span>
                                        )}
                                        <span className="text-[var(--c-accent-ink)]">{shortGameName(raid.game_name)}</span>
                                        <span>•</span>
                                        <span>{raid.guild_server_name}</span>
                                        {raid.difficulty && <span className="px-1.5 py-0.5 rounded border border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]">{raid.difficulty}</span>}
                                        {raid.size && <span className="px-1.5 py-0.5 rounded border border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]">{raid.size}-Man</span>}
                                    </div>
                                </div>
                            </div>

                            {/* Title */}
                            <div>
                                <h1 className="text-3xl sm:text-5xl text-[var(--c-ink)] leading-none" style={DISPLAY}>{raid.name || "Operation"}</h1>
                            </div>

                            {/* Session Time (UTC instants → viewer zone via formatRaidTime) */}
                            <div className="flex flex-wrap items-center gap-2">
                                {sessionChips.map((s: string, i: number) => (
                                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--c-accent-soft)] border border-[var(--c-accent-line)] text-[var(--c-accent-ink)] text-xs font-bold">
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                        {s}
                                    </div>
                                ))}
                                {sessionChips.length > 0 && (
                                  <span className="text-[10px] text-[var(--c-ink-4)] font-medium">{formatIanaTzShortLabel(userTz)}</span>
                                )}
                            </div>

                            {/* Leaders + SR Status */}
                            <div className="flex flex-wrap items-center gap-4 text-xs pt-1">
                                {raid.raid_leader?.character_name && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-purple-600 dark:text-purple-400 font-bold uppercase text-[10px] tracking-wider">Raid Leader</span>
                                        <span className="text-[var(--c-ink)] font-medium bg-[var(--c-surface)] px-2 py-1 rounded border border-[var(--c-line)]">{raid.raid_leader.character_name}</span>
                                    </div>
                                )}
                                {raid.loot_master?.character_name && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-amber-700 dark:text-amber-400 font-bold uppercase text-[10px] tracking-wider">Loot Master</span>
                                        <span className="text-[var(--c-ink)] font-medium bg-[var(--c-surface)] px-2 py-1 rounded border border-[var(--c-line)]">{raid.loot_master.character_name}</span>
                                    </div>
                                )}
                                {srStatus?.enabled && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-cyan-700 dark:text-cyan-400 font-bold uppercase text-[10px] tracking-wider">Soft Reserves</span>
                                        <span className={`font-medium px-2 py-1 rounded border text-xs ${
                                            srStatus.myCount >= srStatus.max
                                                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                                                : 'bg-cyan-500/10 border-cyan-500/20 text-cyan-800 dark:text-cyan-200'
                                        }`}>
                                            {srStatus.myCount}/{srStatus.max}
                                            {srStatus.locked && <span className="ml-1 text-red-600/70 dark:text-red-400/70 text-[9px]">&#x1F512;</span>}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Raid Notes */}
                            {raid.metadata?.notes && (
                                <div className="mt-2 px-3 py-2 rounded-lg bg-[var(--c-bg-veil)] border border-[var(--c-line-soft)]">
                                    <span className="text-[var(--c-ink-4)] font-bold uppercase text-[10px] tracking-wider">Notes</span>
                                    <p className="text-[var(--c-ink-2)] text-xs mt-1 whitespace-pre-wrap leading-relaxed">{raid.metadata.notes}</p>
                                </div>
                            )}
                            </div>

                            {/* Right: Save / Publish / Close */}
                        <div className="flex flex-col gap-3 shrink-0 self-start">
                            <div className="flex items-center gap-3 justify-end">
                            <div className="flex items-center gap-0">
                                {canManage && (
                                    <button 
                                        onClick={handleSaveRoster} 
                                        disabled={!isDirty} 
                                        className={`h-10 px-6 rounded-l-lg font-bold text-xs uppercase tracking-wide transition-all flex items-center justify-center gap-2 ${isDirty ? "bg-emerald-600 text-white hover:bg-emerald-500 shadow-lg shadow-emerald-500/20" : "bg-[var(--c-sunken)] text-[var(--c-ink-4)] cursor-not-allowed border border-[var(--c-line)]"}`}
                                    >
                                        {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
                                        {isSaving ? "Saving..." : "Save"}
                                    </button>
                                )}
                                {canManage && (
                                    <button
                                        onClick={async () => {
                                            const newState = !activeRaid?.is_published;
                                            try {
                                                const res = await fetch(`/raids/${encodeURIComponent(activeRaidId!)}/publish`, {
                                                    method: "PATCH", credentials: "include",
                                                    headers: { "Content-Type": "application/json" },
                                                    body: JSON.stringify({ is_published: newState }),
                                                });
                                                if (!res.ok) throw new Error();
                                                setActiveRaid((prev: any) => prev ? { ...prev, is_published: newState } : prev);
                                                fetchRaids();
                                            } catch { alert("Failed to update publish state"); }
                                        }}
                                        className={`h-10 px-4 font-bold text-xs uppercase tracking-wide transition-all flex items-center justify-center gap-2 border-l-0 ${
                                            activeRaid?.is_published
                                                ? "bg-amber-500/10 border border-amber-500/25 border-l-0 text-amber-700/70 dark:text-amber-300/70 hover:text-amber-800 dark:hover:text-amber-200 hover:border-amber-500/40 hover:bg-amber-500/20"
                                                : "bg-[var(--c-accent)] text-[var(--c-ink-on-fill)] hover:bg-[var(--c-accent-hover)] shadow-[var(--c-shadow)]"
                                        }`}
                                    >
                                        {activeRaid?.is_published ? (
                                            <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg> Unpublish</>
                                        ) : (
                                            <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> Publish</>
                                        )}
                                    </button>
                                )}
                                <button 
                                    onClick={() => setMode('list')} 
                                    className="h-10 w-10 bg-[var(--c-sunken)] hover:bg-red-500/15 border border-[var(--c-line-strong)] rounded-r-lg border-l-0 text-[var(--c-ink-3)] hover:text-red-700 dark:hover:text-red-300 transition-colors flex items-center justify-center"
                                    title="Close"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            </div>

                            {/* Publishing a raid and lending your spare raiders
                                out for that week are the same thought, so the
                                release switch lives right under Publish. The
                                raid's own week is ringed when it is one of the
                                two on offer. */}
                            {canManage && activeRaid?.guild_id && (
                                <div className="flex justify-end">
                                    <GuildWeeklyRelease
                                        guildId={activeRaid.guild_id}
                                        context="planner"
                                        highlightAt={raidFirstStart(activeRaid)}
                                        className="rounded-lg border border-[var(--c-line-soft)] bg-[var(--c-bg-veil)] px-3 py-1.5"
                                    />
                                </div>
                            )}
                            </div>
                    </div>

                    {/* ── Raid Controls ── */}
                    <div className="mt-5 pt-5 border-t border-[var(--c-line)]">
                        <div className="flex flex-wrap items-center gap-2">
                            {canManage && (
                                <button 
                                    onClick={() => openEditModal(activeRaidId!)} 
                                    className="h-10 px-4 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] text-[13px] font-semibold shadow-[var(--c-shadow-sm)] transition-all hover:border-[var(--c-line-strong)] hover:text-[var(--c-ink)] hover:shadow-[var(--c-shadow)] flex items-center justify-center gap-2"
                                >
                                    <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                    Edit Raid
                                </button>
                            )}
                            
                            <button 
                                onClick={() => setShowRatingModal(true)} 
                                className="h-10 px-4 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] text-[13px] font-semibold shadow-[var(--c-shadow-sm)] transition-all hover:border-[var(--c-line-strong)] hover:text-[var(--c-ink)] hover:shadow-[var(--c-shadow)] flex items-center justify-center gap-2"
                            >
                                <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                                Rate Players
                            </button>

                            <button
                                onClick={() => setMode("soft-reserve")}
                                className="h-10 px-4 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] text-[13px] font-semibold shadow-[var(--c-shadow-sm)] transition-all hover:border-[var(--c-line-strong)] hover:text-[var(--c-ink)] hover:shadow-[var(--c-shadow)] flex items-center justify-center gap-2"
                            >
                                <svg className="w-4 h-4 text-purple-600 dark:text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                Soft Reserve
                            </button>

                            <button
                                onClick={() => setMode("assignments")}
                                className="h-10 px-4 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] text-[13px] font-semibold shadow-[var(--c-shadow-sm)] transition-all hover:border-[var(--c-line-strong)] hover:text-[var(--c-ink)] hover:shadow-[var(--c-shadow)] flex items-center justify-center gap-2"
                            >
                                <svg className="w-4 h-4 text-teal-600 dark:text-teal-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6m-6 4h6" /></svg>
                                Assignments
                            </button>

                            <button
                                type="button"
                                onClick={handleOpenLootCouncilFromDetail}
                                className="h-10 px-4 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] text-[13px] font-semibold shadow-[var(--c-shadow-sm)] transition-all hover:border-[var(--c-line-strong)] hover:text-[var(--c-ink)] hover:shadow-[var(--c-shadow)] flex items-center justify-center gap-2"
                            >
                                <svg className="w-4 h-4 text-violet-600 dark:text-violet-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a3 3 0 100 6m0-6a3 3 0 110 6m0 0v2m0-2a3 3 0 100 6m0-6a3 3 0 110 6v2m-8-9h2m12 0h-2M7.05 7.05l1.414 1.414m7.072 7.072 1.414 1.414m0-9.9-1.414 1.414m-7.072 7.072-1.414 1.414" />
                                </svg>
                                Loot Council
                            </button>

                            {canManage && (
                                <button
                                    onClick={async () => {
                                        try {
                                            const res = await fetch(`/raids/${encodeURIComponent(activeRaidId!)}/pug-token`, { method: "POST", credentials: "include" });
                                            if (!res.ok) throw new Error();
                                            const data = await res.json();
                                            const url = `${window.location.origin}${window.location.pathname}#pug/${data.invite_code}`;
                                            await navigator.clipboard.writeText(url);
                                            setPugSrCopied(true);
                                            setTimeout(() => setPugSrCopied(false), 2500);
                                        } catch { alert("Failed to generate PUG link"); }
                                    }}
                                    className={`h-10 px-4 rounded-xl border text-[13px] font-semibold transition-all flex items-center justify-center gap-2 ${
                                        pugSrCopied
                                            ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                                            : "border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] shadow-[var(--c-shadow-sm)] hover:border-[var(--c-line-strong)] hover:text-[var(--c-ink)]"
                                    }`}
                                >
                                    <svg className="w-4 h-4 text-orange-600 dark:text-orange-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                    {pugSrCopied ? "Link Copied!" : "PUG Invite Link"}
                                </button>
                            )}

                            {/* Leadership, inline with the raid actions. Only characters
                                seated in this raid who opted into the role are offered.
                                The native select is stretched invisibly over the whole
                                control, so clicking the label, the name or the arrow all
                                open the list. */}
                            {canManage && (
                            <>
                            {/* Rule between the actions and the leadership group. */}
                            <span className="mx-1 hidden h-6 w-px bg-[var(--c-line-strong)] sm:block" aria-hidden="true" />
                            <div className="flex h-10 items-center rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] p-1">
                            {([
                                { key: "leader", label: "Raid Leader", tone: "text-purple-600 dark:text-purple-400", value: editRaidLeader,
                                  set: setEditRaidLeader, eligible: (a: any) => a.can_raid_lead || a.character_id === editRaidLeader },
                                { key: "loot", label: "Loot Master", tone: "text-amber-700 dark:text-amber-400", value: editLootMaster,
                                  set: setEditLootMaster, eligible: (a: any) => a.can_loot_master || a.character_id === editLootMaster },
                            ] as const).map((ctl, i) => {
                                const current = activeAssignments.find((a: any) => a.character_id === ctl.value);
                                return (
                                    <React.Fragment key={ctl.key}>
                                    {i > 0 && <span className="mx-1 h-5 w-px bg-[var(--c-line-strong)]" aria-hidden="true" />}
                                    <div
                                        className="relative flex h-full cursor-pointer items-center gap-2 rounded-lg px-3 transition-colors hover:bg-[var(--c-surface)] focus-within:bg-[var(--c-surface)] focus-within:shadow-[var(--c-shadow-sm)]"
                                    >
                                        <span className={`text-[11px] font-bold uppercase tracking-wider ${ctl.tone}`}>{ctl.label}</span>
                                        <span className={`whitespace-nowrap text-[13px] font-semibold ${current ? "text-[var(--c-ink)]" : "text-[var(--c-ink-3)]"}`}>
                                            {current?.character_name || "None"}
                                        </span>
                                        <svg className="h-3.5 w-3.5 shrink-0 text-[var(--c-ink-3)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                                        <select
                                            aria-label={ctl.label}
                                            value={ctl.value}
                                            onChange={(e) => { ctl.set(e.target.value); setIsDirty(true); }}
                                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                        >
                                            <option value="">None</option>
                                            {activeAssignments.filter(ctl.eligible).map((a: any) => (
                                                <option key={a.character_id} value={a.character_id}>{a.character_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    </React.Fragment>
                                );
                            })}
                            </div>
                            </>
                            )}

                            {canManage && (
                                <>
                                    <div className="flex-1" />
                                    <button 
                                        onClick={() => handleDeleteRaid(activeRaidId!)} 
                                        className="h-10 px-4 text-[var(--c-ink-3)] hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/15 border border-[var(--c-line)] hover:border-red-500/30 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2"
                                        title="Delete Raid"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        Delete
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    </div>
            </div>

            {/* CROSS-GUILD NOTICES BANNER — officers / RL / ML only.
                Shows one row per notice; muted + strikethrough for 'declined'.
                Top button DMs every notice that has not yet been notified. */}
            {canManage && allCrossGuildNotices.length > 0 && (
                <div className="mb-4 rounded-xl border border-purple-500/30 bg-purple-500/10 overflow-hidden">
                    <div className={`flex items-center justify-between px-4 py-3 bg-purple-500/5 ${crossGuildCollapsed ? '' : 'border-b border-purple-500/20'}`}>
                        <button
                            type="button"
                            onClick={() => setCrossGuildCollapsed(v => !v)}
                            aria-expanded={!crossGuildCollapsed}
                            className="flex items-center gap-2 min-w-0 group/xg text-left"
                        >
                            <svg
                                className={`w-3.5 h-3.5 text-purple-700/70 dark:text-purple-300/70 shrink-0 transition-transform group-hover/xg:text-purple-800 dark:group-hover/xg:text-purple-200 ${crossGuildCollapsed ? '-rotate-90' : ''}`}
                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                            <span className="text-purple-700 dark:text-purple-300 font-bold text-xs uppercase tracking-wider shrink-0 group-hover/xg:text-purple-800 dark:group-hover/xg:text-purple-200">Cross-Guild Invitations</span>
                            <span className="text-[10px] text-purple-700/60 dark:text-purple-300/60 truncate">
                                {allCrossGuildNotices.filter(n => !n.notified_at).length} pending
                                {' · '}
                                {allCrossGuildNotices.filter(n => n.notified_at && !n.response).length} awaiting
                                {' · '}
                                {allCrossGuildNotices.filter(n => n.response === 'accepted').length} accepted
                            </span>
                        </button>
                        {(() => {
                            const unnotifiedUserIds = allCrossGuildNotices
                                .filter(n => !n.notified_at)
                                .map(n => n.user_id);
                            if (unnotifiedUserIds.length === 0) return null;
                            return (
                                <button
                                    onClick={() => {
                                        setNotifyLootSystem('soft_reserve');
                                        setNotifyModalUserIds(unnotifiedUserIds);
                                        setNotifyModalOpen(true);
                                    }}
                                    className="h-7 px-3 rounded bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-bold uppercase tracking-wider transition-colors shrink-0"
                                >
                                    Send DM ({unnotifiedUserIds.length})
                                </button>
                            );
                        })()}
                    </div>
                    {!crossGuildCollapsed && (
                    <div className="divide-y divide-purple-500/10">
                        {allCrossGuildNotices.map(n => {
                            const isDeclined = n.response === 'declined';
                            const isAccepted = n.response === 'accepted';
                            const isSent     = !!n.notified_at && !n.response;
                            // A pending row hasn't been to the server yet, so whether the
                            // player has Discord linked is still unknown. Keep it sendable:
                            // the send saves first and the real state comes back with the row.
                            const noDiscord  = !n.discord_id && !n.pending;
                            const stateLabel = isDeclined ? 'Declined'
                                : isAccepted ? 'Accepted'
                                : isSent     ? 'DM Sent'
                                : noDiscord  ? 'No Discord Linked'
                                :              'Not Notified';
                            const stateClass = isDeclined ? 'text-red-700 dark:text-red-300 bg-red-500/20 border-red-500/30'
                                : isAccepted ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/20 border-emerald-500/30'
                                : isSent     ? 'text-sky-700 dark:text-sky-300 bg-sky-500/20 border-sky-500/30'
                                : noDiscord  ? 'text-amber-700 dark:text-amber-300 bg-amber-500/20 border-amber-500/30'
                                :              'text-[var(--c-ink-3)] bg-[var(--c-sunken)] border-[var(--c-line)]';
                            return (
                                <div
                                    key={n.id}
                                    className={`flex items-center gap-3 px-4 py-2.5 ${isDeclined ? 'opacity-40 line-through' : ''}`}
                                >
                                    <span className="text-xs font-bold truncate" style={{ color: getClassColor(n.class_name || '') }}>
                                        {n.character_name}
                                    </span>
                                    <span className="text-[10px] text-[var(--c-ink-3)] truncate">
                                        {[n.user_display_name, n.source_guild_id ? n.source_guild_tag : 'Free Agent'].filter(Boolean).join(' · ')}
                                    </span>
                                    <span className={`ml-auto px-2 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider shrink-0 ${stateClass}`}>
                                        {stateLabel}
                                    </span>
                                    {!isDeclined && !isAccepted && !noDiscord && (
                                        <button
                                        onClick={() => {
                                            setNotifyLootSystem('soft_reserve');
                                            setNotifyModalUserIds([n.user_id]);
                                            setNotifyModalOpen(true);
                                        }}
                                            className="h-6 px-2 rounded bg-purple-500/20 hover:bg-purple-500/40 border border-purple-500/30 text-purple-800 dark:text-purple-200 text-[9px] font-bold uppercase tracking-wider transition-colors shrink-0"
                                        >
                                            {isSent ? 'Resend' : 'Send DM'}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    )}
                </div>
            )}

            <div className="grid gap-6 items-start lg:grid-cols-[360px_minmax(0,1fr)] 2xl:grid-cols-[400px_minmax(0,1fr)]">
                <div className="min-w-0 space-y-4 order-1 lg:order-2">
                    {/* Compact Composition Summary */}
                    {(() => {
                        const roleCounts: Record<string, number> = { "Tank": 0, "Healer": 0, "Melee DPS": 0, "Ranged DPS": 0 };
                        const classCounts: Record<string, number> = {};
                        
                        const seatedForStats = activeAssignments.filter((a: any) => !a.metadata?.is_bench);
                        seatedForStats.forEach((a: any) => {
                            const role = a.role_label || a.role || "";
                            if (role.includes("Tank")) roleCounts["Tank"]++;
                            else if (role.includes("Healer")) roleCounts["Healer"]++;
                            else if (role.includes("Melee")) roleCounts["Melee DPS"]++;
                            else if (role.includes("Ranged")) roleCounts["Ranged DPS"]++;
                            
                            const className = a.character_class_name || a.class || "Unknown";
                            classCounts[className] = (classCounts[className] || 0) + 1;
                        });
                        
                        const sortedClasses = Object.entries(classCounts).sort((a, b) => b[1] - a[1]);
                        const totalAssigned = seatedForStats.length;
                        
                        return (
                            <div className="flex flex-wrap items-center gap-3 px-1 py-2">
                                {/* Role counts */}
                                <div className="flex items-center gap-2 text-xs">                                    
                                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)]">
                                        <span>🛡️</span><span className="font-bold text-[var(--c-ink)]">{roleCounts["Tank"]}</span>
                                    </span>
                                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)]">
                                        <span>💚</span><span className="font-bold text-[var(--c-ink)]">{roleCounts["Healer"]}</span>
                                    </span>
                                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)]">
                                        <span>⚔️</span><span className="font-bold text-[var(--c-ink)]">{roleCounts["Melee DPS"]}</span>
                                    </span>
                                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)]">
                                        <span>🏹</span><span className="font-bold text-[var(--c-ink)]">{roleCounts["Ranged DPS"]}</span>
                                    </span>
                                </div>
                                
                                {/* Divider */}
                                {sortedClasses.length > 0 && <span className="text-[var(--c-ink-4)]">|</span>}
                                
                                {/* Class breakdown */}
                                <div className="flex flex-wrap items-center gap-1">
                                    {sortedClasses.map(([className, count]) => (
                                        <span 
                                        key={className}
                                        className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-surface)] border border-[var(--c-line-soft)] text-[10px]"
                                        title={className}
                                    >
                                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getClassColor(className) }} />
                                        <span className="font-bold" style={{ color: getClassColor(className) }}>{count}</span>
                                    </span>
                                    ))}
                                </div>
                                
                                {/* Total */}
                                <span className="text-[10px] text-[var(--c-ink-4)] ml-auto">{totalAssigned}/{size}</span>
                            </div>
                        );
                    })()}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {chunkRoster(rosterArray, size).map((grp, gIdx) => (
                            <div key={gIdx} className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-2xl p-2.5 shadow-[var(--c-shadow-sm)]">
                                <div className="space-y-1">
                                    {grp.map((assignment: any, slotIdx: number) => {
                                        const globalIdx = gIdx * 5 + slotIdx;
                                        return (
                                            <div key={globalIdx} ref={el => { slotRefs.current[globalIdx] = el; }}>
                                                {assignment ? (() => {
                                                    const assignmentUserId = assignment.character_user_id || assignment.user_id || assignment.userId;
                                                    const assignmentSquadInfo = assignmentUserId ? squadLookup[assignmentUserId] : null;
                                                    const isSquadmateHovered = !!(hoveredSquadId && assignmentSquadInfo?.squadId === hoveredSquadId);

                                                    // Cross-guild notice state for this seat. Match by user_id (the notice
                                                    // table is keyed on guild_raid_id + user_id). Seats dropped since the
                                                    // last save match a pending mirror row, whose Discord link is still
                                                    // unknown, so they read as unsent rather than 'No Discord linked'.
                                                    const xgNotice = assignmentUserId
                                                        ? allCrossGuildNotices.find(n => n.user_id === assignmentUserId)
                                                        : null;
                                                    let xgState: null | { key: string; color: string; label: string } = null;
                                                    if (xgNotice) {
                                                        if (xgNotice.response === 'accepted')      xgState = { key: 'accepted',    color: '#10b981', label: 'Accepted' };
                                                        else if (xgNotice.response === 'declined') xgState = { key: 'declined',    color: '#ef4444', label: 'Declined' };
                                                        else if (!xgNotice.discord_id && !xgNotice.pending)
                                                                                                   xgState = { key: 'no_discord',  color: '#f59e0b', label: 'No Discord linked' };
                                                        else if (xgNotice.notified_at)             xgState = { key: 'sent',        color: '#38bdf8', label: 'DM sent — awaiting response' };
                                                        else                                       xgState = { key: 'unsent',      color: '#9ca3af', label: 'Not notified yet' };
                                                    } else if (assignment.isCrossGuild) {
                                                        xgState = { key: 'unsent', color: '#9ca3af', label: 'Not notified yet' };
                                                    }

                                                    return (
                                                        <DraggableCard 
                                                        character={{ name: assignment.name || assignment.character_name, className: assignment.class || assignment.character_class_name, role_label: assignment.role || assignment.role_label, isPug: assignment.is_pug || false }}
                                                        wclScore={sourceRoster.find((c: any) => c.characterId === assignment.character_id)?.metadata?.wcl_perf?.score}
                                                        crossGuildState={xgState}
                                                        classIconUrl={(() => { const cn = assignment.class || assignment.character_class_name; const charData = sourceRoster.find((c: any) => c.characterId === assignment.character_id); if (charData && currentGameFields) { const entries = resolveSpecEntries(charData, currentGameFields); const rl = assignment.role || assignment.role_label; const m = entries.find((e: any) => e.roleLabel === rl); const specUrl = getSpecIconUrl(m?.specLabel || entries[0]?.specLabel, cn, currentGameFields); if (specUrl) return specUrl; } return getClassIconUrl(cn, currentGameFields); })()}
                                                        role={assignment.role || assignment.role_label}
                                                        specLabel={(() => {
                                                          const charData = sourceRoster.find((c: any) => c.characterId === assignment.character_id);
                                                          if (!charData || !currentGameFields) return null;
                                                          const entries = resolveSpecEntries(charData, currentGameFields);
                                                          const roleLabel = assignment.role || assignment.role_label;
                                                          const match = entries.find((e: any) => e.roleLabel === roleLabel);
                                                          return match?.specLabel || entries[0]?.specLabel || null;
                                                        })()}
                                                        roleColor={(() => {
                                                          const charData = sourceRoster.find((c: any) => c.characterId === assignment.character_id);
                                                          if (!charData || !currentGameFields) return null;
                                                          return getRoleColorForRoleLabel(charData, currentGameFields, assignment.role || assignment.role_label);
                                                        })()}
                                                        isInteractive={canManage}
                                                        isDraggingFromHere={false}
                                                        squadInfo={assignmentSquadInfo}
                                                        isSquadmateHovered={isSquadmateHovered}
                                                        onMouseEnter={() => { if (assignmentSquadInfo) setHoveredSquadId(assignmentSquadInfo.squadId); }}
                                                        onMouseLeave={() => setHoveredSquadId(null)}
                                                        onMouseDown={(e: any) => {
                                                            if (!canManage || e.button !== 0) return;
                                                            setDragOrigin({ type: 'slot', id: assignment.id || assignment.character_id, index: globalIdx });
                                                        }}
                                                        onRemove={() => handleUnassign(globalIdx)}
                                                        selfRemovable={!canManage && assignmentUserId === me?.id && raidNotStarted}
                                                        onSelfRemove={handleLeaveRaid}
                                                        />);
                                                    })() : <div className="h-[48px] border border-dashed border-[var(--c-line)] rounded flex items-center justify-center text-xs text-[var(--c-ink-4)]">Empty</div>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                        </div>
    
                        {/* TRUE BENCH: backup players. Locked + bilocation-counted like raiders, but outside the seats.
                            Open Bench flips that: while open, benched players become recruitable by other raid teams. */}
                        <div ref={benchZoneRef} className={`mt-4 rounded-xl border border-dashed p-3 transition-colors ${(raid.metadata?.open_bench) ? "border-emerald-400/40 bg-emerald-400/[0.04]" : "border-amber-400/30 bg-amber-400/[0.03]"}`}>
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700/80 dark:text-amber-300/80">Bench</span>
                                <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-800/90 dark:text-amber-200/90">{activeAssignments.filter((a: any) => a.metadata?.is_bench).length}</span>
                                {canManage && <span className="text-[10px] text-[var(--c-ink-4)]">Drag a player here to bench them; drag onto a seat to promote.</span>}

                                {/* OPEN BENCH: toggle for leaders, status badge for everyone else */}
                                {(() => {
                                    const openBench = !!raid.metadata?.open_bench;
                                    if (!canManage) {
                                        return openBench ? (
                                            <span className="ml-auto rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300" title="The bench is open: other raid teams can see and recruit these players.">
                                                🔓 Open Bench
                                            </span>
                                        ) : null;
                                    }
                                    const toggleOpenBench = async () => {
                                        const next = !openBench;
                                        try {
                                            const res = await fetch(`/raids/${raid.id}/open-bench`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                credentials: 'include',
                                                body: JSON.stringify({ open: next }),
                                            });
                                            if (!res.ok) throw new Error();
                                            setActiveRaid((prev: any) => prev ? { ...prev, metadata: { ...(prev.metadata || {}), open_bench: next } } : prev);
                                            showToast(next
                                                ? 'Bench opened. Other raid teams can now see and recruit your benched players.'
                                                : 'Bench closed. Your benched players are reserved for this raid again.');
                                        } catch {
                                            showToast('Failed to update Open Bench', 'error');
                                        }
                                    };
                                    return (
                                        <span className="group/ob relative ml-auto flex items-center gap-2">
                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${openBench ? 'text-emerald-700 dark:text-emerald-300' : 'text-[var(--c-ink-3)]'}`}>
                                                {openBench ? '🔓' : '🔒'} Open Bench
                                            </span>
                                            {/* info icon: hover or focus reveals the explainer */}
                                            <span
                                                tabIndex={0}
                                                className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-[var(--c-line-strong)] text-[9px] font-bold text-[var(--c-ink-3)] outline-none hover:border-[var(--c-ink-4)] hover:text-[var(--c-ink)] focus:border-[var(--c-ink-4)] focus:text-[var(--c-ink)]"
                                            >
                                                ?
                                            </span>
                                            {/* toggle switch */}
                                            <button
                                                onClick={toggleOpenBench}
                                                aria-label={openBench ? 'Close bench to other raid teams' : 'Open bench to other raid teams'}
                                                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${openBench ? 'bg-emerald-500' : 'bg-[var(--c-sunken-2)]'}`}
                                            >
                                                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${openBench ? 'translate-x-[18px]' : 'translate-x-1'}`} />
                                            </button>
                                            {/* Tooltip: what it does and what can happen. Opens UPWARD: the bench
                                                panel sits near the bottom of the page, so a downward tooltip gets
                                                cut off by the viewport; the seat grid above always has room. */}
                                            <div className="pointer-events-none absolute right-0 bottom-full mb-2 z-30 hidden w-80 rounded-xl border border-[var(--c-line-strong)] bg-[var(--c-surface)] p-3.5 text-left shadow-2xl group-hover/ob:block group-focus-within/ob:block">
                                                <div className="mb-1.5 text-[11px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-300">🔓 Open Bench</div>
                                                <p className="mb-2 text-[11px] leading-relaxed text-[var(--c-ink-2)]">
                                                    While on, the players on this bench are offered to other raid teams scheduled at the same time, instead of being blocked as "In Another Raid".
                                                </p>
                                                <ul className="mb-2 space-y-1.5 text-[11px] leading-relaxed text-[var(--c-ink-2)]">
                                                    <li className="flex gap-1.5"><span className="text-emerald-700 dark:text-emerald-400">•</span><span>Other teams in your guild see them as available in their roster pool.</span></li>
                                                    <li className="flex gap-1.5"><span className="text-emerald-700 dark:text-emerald-400">•</span><span>Other guilds see them under "Available from Other Teams" (only players who opted in, and only if your guild released the week).</span></li>
                                                    <li className="flex gap-1.5"><span className="text-amber-700 dark:text-amber-400">•</span><span className="text-amber-800/90 dark:text-amber-200/90">If another team seats one of them, they are automatically removed from this bench. You'll see who took them, and when, under Roster changes.</span></li>
                                                </ul>
                                                <p className="text-[11px] leading-relaxed text-[var(--c-ink-3)]">
                                                    While off, benched players stay reserved as backups for this raid.
                                                </p>
                                            </div>
                                        </span>
                                    );
                                })()}
                            </div>
                            {(() => {
                                const benchAssignments = activeAssignments.filter((a: any) => a.metadata?.is_bench);
                                if (benchAssignments.length === 0) {
                                    return <div className="flex min-h-[104px] items-center justify-center text-center text-[11px] text-[var(--c-ink-4)]">{canManage ? "Drag players here to add backups." : "No bench players."}</div>;
                                }
                                return (
                                    <div className="grid min-h-[104px] content-start grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-2">
                                        {benchAssignments.map((assignment: any) => {
                                            const assignmentUserId = assignment.character_user_id || assignment.user_id || assignment.userId;
                                            const assignmentSquadInfo = assignmentUserId ? squadLookup[assignmentUserId] : null;
                                            const charData = sourceRoster.find((c: any) => c.characterId === assignment.character_id);
                                            return (
                                                <DraggableCard
                                                    key={`bench-${assignment.id || assignment.character_id}`}
                                                    character={{ name: assignment.name || assignment.character_name, className: assignment.class || assignment.character_class_name, role_label: assignment.role || assignment.role_label, isPug: assignment.is_pug || assignment.metadata?.is_pug || false }}
                                                    wclScore={charData?.metadata?.wcl_perf?.score}
                                                    classIconUrl={(() => { const cn = assignment.class || assignment.character_class_name; if (charData && currentGameFields) { const entries = resolveSpecEntries(charData, currentGameFields); const rl = assignment.role || assignment.role_label; const m = entries.find((e: any) => e.roleLabel === rl); const specUrl = getSpecIconUrl(m?.specLabel || entries[0]?.specLabel, cn, currentGameFields); if (specUrl) return specUrl; } return getClassIconUrl(cn, currentGameFields); })()}
                                                    role={assignment.role || assignment.role_label}
                                                    specLabel={(() => { if (!charData || !currentGameFields) return null; const entries = resolveSpecEntries(charData, currentGameFields); const roleLabel = assignment.role || assignment.role_label; const match = entries.find((e: any) => e.roleLabel === roleLabel); return match?.specLabel || entries[0]?.specLabel || null; })()}
                                                    roleColor={(() => { if (!charData || !currentGameFields) return null; return getRoleColorForRoleLabel(charData, currentGameFields, assignment.role || assignment.role_label); })()}
                                                    isInteractive={canManage}
                                                    isDraggingFromHere={false}
                                                    squadInfo={assignmentSquadInfo}
                                                    onMouseDown={(e: any) => { if (!canManage || e.button !== 0) return; setDragOrigin({ type: 'benchseat', id: assignment.id || assignment.character_id }); }}
                                                    onRemove={() => handleBenchRemove(assignment.id || assignment.character_id)}
                                                    selfRemovable={!canManage && assignmentUserId === me?.id && raidNotStarted}
                                                    onSelfRemove={handleLeaveRaid}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* OPEN SEATS: offer seats to any qualifying player (leaders only). */}
                        <OpenSeatsPanel
                            raidId={raid.id}
                            canManage={canManage}
                            isDirty={isDirty}
                            onSeated={() => openRaidDetail(raid.id)}
                        />

                        {/* ROSTER CHANGES TIMELINE: who joined/left/was removed and why.
                            Officers can correct a reason after the fact via the dropdown. */}
                        {rosterEventLog && rosterEventLog.events.length > 0 && (
                            <div className="mt-4 rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-3 shadow-[var(--c-shadow-sm)]">
                                <button onClick={() => setRosterLogOpen(o => !o)} className="flex w-full items-center gap-2 text-left">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--c-ink-3)]">Roster changes</span>
                                    <span className="rounded-full bg-[var(--c-sunken-2)] px-2 py-0.5 text-[10px] font-bold text-[var(--c-ink-2)]">{rosterEventLog.events.length}</span>
                                    <span className="ml-auto text-xs text-[var(--c-ink-4)]">{rosterLogOpen ? '▾ hide' : '▸ show'}</span>
                                </button>
                                {rosterLogOpen && (
                                    <div className="mt-2 max-h-64 space-y-1 overflow-y-auto pr-1">
                                        {rosterEventLog.events.map((ev: any) => {
                                            const actionLabel =
                                                ev.action === 'added' ? 'added' :
                                                ev.action === 'removed' ? 'removed' :
                                                ev.action === 'left' ? 'left the raid' :
                                                ev.action === 'declined_invite' ? 'declined the invite' :
                                                ev.action === 'kicked_from_guild' ? 'removed from guild' : ev.action;
                                            const when = ev.occurred_at ? new Date(ev.occurred_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                                            const editable = rosterEventLog.canManage && ev.action !== 'added';
                                            return (
                                                <div key={ev.id} className="flex items-center gap-2 rounded-lg bg-[var(--c-bg-veil)] px-2 py-1.5 text-xs">
                                                    <span className="whitespace-nowrap text-[10px] text-[var(--c-ink-4)]">{when}</span>
                                                    <span className="font-bold" style={{ color: getClassColor(ev.character_class_name || '') }}>{ev.character_name || 'Unknown'}</span>
                                                    <span className="text-[var(--c-ink-3)]">{actionLabel}</span>
                                                    {ev.actor_display_name && ev.action !== 'left' && ev.action !== 'declined_invite' && (
                                                        <span className="text-[var(--c-ink-4)]">by {ev.actor_display_name}</span>
                                                    )}
                                                    {ev.note && <span className="truncate text-[var(--c-ink-3)]" title={ev.note}>“{ev.note}”</span>}
                                                    <span className="ml-auto flex items-center gap-1.5">
                                                        {ev.counts_against && (
                                                            <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold text-red-700 dark:text-red-300">counts</span>
                                                        )}
                                                        {ev.action === 'removed' && (editable ? (
                                                            <select
                                                                value={ev.reason_code || 'unspecified'}
                                                                onChange={e => updateRosterEventReason(ev.id, e.target.value)}
                                                                className="rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--c-ink-2)]"
                                                            >
                                                                <option value="unspecified">Unspecified</option>
                                                                {REMOVAL_REASON_OPTIONS.map(r => (
                                                                    <option key={r.code} value={r.code}>{r.label}</option>
                                                                ))}
                                                                {SYSTEM_REMOVAL_REASON_LABELS[ev.reason_code] && (
                                                                    <option value={ev.reason_code}>{SYSTEM_REMOVAL_REASON_LABELS[ev.reason_code]}</option>
                                                                )}
                                                            </select>
                                                        ) : (
                                                            ev.reason_code && ev.reason_code !== 'unspecified' && (
                                                                <span className="rounded-full bg-[var(--c-sunken-2)] px-2 py-0.5 text-[10px] font-bold text-[var(--c-ink-2)]">
                                                                    {REMOVAL_REASON_OPTIONS.find(r => r.code === ev.reason_code)?.label || SYSTEM_REMOVAL_REASON_LABELS[ev.reason_code] || ev.reason_code}
                                                                </span>
                                                            )
                                                        ))}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

    {/* Buff Coverage Panel */}
{compositionTags.length > 0 && activeAssignments.length > 0 && (() => {
  // Build charById from candidates (which have options)
  const charLookup = new Map<string, any>();
  sourceRoster.forEach((c: any) => charLookup.set(c.characterId, c));
  
  // Build slot arrays from assignments
  const slotCharIds: (string | null)[] = new Array(size).fill(null);
  const slotRoles: (string | null)[] = new Array(size).fill(null);
  activeAssignments.forEach((a: any) => {
    const idx = a.metadata?.seat_index !== undefined ? Number(a.metadata.seat_index) : -1;
    if (idx >= 0 && idx < size) {
      slotCharIds[idx] = a.character_id;
      slotRoles[idx] = a.role_label;
    }
  });
  
  const coverage = computeBuffCoverage(slotCharIds, slotRoles, charLookup, currentGameFields, compositionTags);
  if (coverage.length === 0) return null;
  
  const totalCovered = coverage.reduce((s, g) => s + g.coveredCount, 0);
  const totalAll = coverage.reduce((s, g) => s + g.totalCount, 0);
  const pct = totalAll > 0 ? Math.round((totalCovered / totalAll) * 100) : 0;

  // Average WCL parse across seated characters (only counts those with a score)
  const seatedScores: number[] = [];
  activeAssignments.forEach((a: any) => {
    const idx = a.metadata?.seat_index !== undefined ? Number(a.metadata.seat_index) : -1;
    if (idx < 0 || idx >= size) return; // bench-only assignments excluded
    const charData = charLookup.get(a.character_id);
    const score = charData?.metadata?.wcl_perf?.score;
    if (typeof score === 'number' && score > 0) seatedScores.push(score);
  });
  const avgParse = seatedScores.length > 0
    ? Math.round(seatedScores.reduce((s, n) => s + n, 0) / seatedScores.length)
    : null;
  
  return (
    <div className="mt-4 rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)]">
      <button
        onClick={() => setBuffPanelOpen(!buffPanelOpen)}
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[var(--c-sunken)] transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-2)]">Composition Coverage</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pct >= 80 ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : pct >= 50 ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300' : 'bg-red-500/20 text-red-700 dark:text-red-300'}`}>
            {totalCovered}/{totalAll} ({pct}%)
          </span>
          {avgParse !== null && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[var(--c-sunken-2)] font-mono flex items-center gap-1"
              style={{ color: getParseColor(avgParse) }}
              title={`Average Warcraft Logs parse across ${seatedScores.length} seated character${seatedScores.length === 1 ? '' : 's'}`}
            >
              <span className="text-[var(--c-ink-3)] font-sans">AVG PARSE</span>
              {avgParse}
            </span>
          )}
        </div>
        <svg className={`w-4 h-4 text-[var(--c-ink-3)] transition-transform ${buffPanelOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {buffPanelOpen && (
        <div className="px-5 pb-5 space-y-3 border-t border-[var(--c-line-soft)] columns-2 gap-6">
          {coverage.map((group) => (
            <div key={`${group.category}-${group.subcategory || ''}`} className="pt-3 break-inside-avoid">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--c-ink-3)]">
                  {group.subcategory || group.category}
                </span>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${group.coveredCount === group.totalCount ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-[var(--c-sunken)] text-[var(--c-ink-4)]'}`}>
                  {group.coveredCount}/{group.totalCount}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {group.tags.map((tc) => (
                  <span
                    key={tc.tag.tag_key}
                    title={tc.covered ? `${tc.tag.description || tc.tag.tag_label}: ${tc.providers.join(', ')}` : `${tc.tag.description || tc.tag.tag_label}: MISSING`}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border transition-all ${
                      hoveredSpecTags.size > 0 && hoveredSpecTags.has(tc.tag.tag_key)
                        ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-800 dark:text-cyan-200 ring-1 ring-cyan-400/30'
                        : tc.covered
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
                          : 'bg-[var(--c-bg-veil)] border-[var(--c-line-soft)] text-[var(--c-ink-4)]'
                    }`}
                  >
                    {tc.tag.tag_label}
                    {tc.covered && tc.providers.length > 1 && (
                      <span className="text-[9px] text-emerald-700/60 dark:text-emerald-400/60">{tc.providers.length}</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
})()}
</div>

<div className="order-2 min-w-0 space-y-4 lg:order-1 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1 scrollbar-thin scrollbar-thumb-white/10">
                    {/* BENCH SECTION - Visible to all, editable by managers */}
                    <div className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-2xl p-4 shadow-[var(--c-shadow-sm)]">
                        <div className="flex justify-between mb-2">
                        <h3 className="text-xs font-bold uppercase text-[var(--c-ink-2)]">Roster Pool</h3>
                            {canManage && (
                                <button onClick={handleAutoFill} className="text-cyan-700 dark:text-cyan-400 text-xs border border-cyan-500/30 px-2 py-1 rounded bg-cyan-500/10 hover:bg-cyan-500/20">
                                    Check Suggestions
                                </button>
                            )}
                        </div>
                        <div className="space-y-4">
                            {/* Available Players */}
                            {(() => {
                                const available = ownBench.filter((c: any) => c.isAvailable);
                                if (available.length === 0) return null;
                                return (
                                    <div className="space-y-2">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700/70 dark:text-emerald-400/70 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                            Available ({available.length})
                                        </div>
                                        {available.map((c: any) => {
                                            const availableRoles = c.roles || c.metadata?.role_labels || ["DPS"];
                                            const charSquadInfo = c.userId ? squadLookup[c.userId] : null;
                                            const isSquadmateHovered = !!(hoveredSquadId && charSquadInfo?.squadId === hoveredSquadId);
                                            return (
                                                <DraggableCard 
                                                    key={c.characterId} 
                                                    character={{ name: c.name, className: c.metadata?.class_name, isPug: c.isPug || c.metadata?.is_pug, isCrossGuild: c.isCrossGuild, sourceGuildName: c.sourceGuildName, sourceGuildTag: c.sourceGuildTag }}
                                                    wclScore={c.metadata?.wcl_perf?.score}
                                                    classIconUrl={getClassIconUrl(c.metadata?.class_name, currentGameFields)}
                                                    availableRoles={availableRoles}
                                                    specEntries={currentGameFields ? resolveSpecEntries(c, currentGameFields) : null}
                                                    unavailable={false}
                                                    isInteractive={canManage}
                                                    squadInfo={charSquadInfo}
                                                    isSquadmateHovered={isSquadmateHovered}
                                                    onMouseEnter={() => {
                                                      if (charSquadInfo) setHoveredSquadId(charSquadInfo.squadId);
                                                      if (currentGameFields) {
                                                        const tags = getCharacterProvidedTags(c, currentGameFields, null);
                                                        setHoveredSpecTags(tags.length > 0 ? new Set(tags) : new Set());
                                                      }
                                                    }}
                                                    onMouseLeave={() => { setHoveredSquadId(null); setHoveredSpecTags(new Set()); }}
                                                    onMouseDown={(e: any) => {
                                                        if (e.button !== 0) return;
                                                        setDragOrigin({ type: 'bench', id: c.characterId });
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            })()}

                            {/* Unavailable Buckets */}
                            {(() => {
                                const unavailable = ownBench.filter((c: any) => !c.isAvailable);
                                if (unavailable.length === 0) return null;

                                // Group by reason
                                const buckets: Record<string, any[]> = {};
                                unavailable.forEach((c: any) => {
                                    const reason = c.unavailableReason || "Unavailable";
                                    if (!buckets[reason]) buckets[reason] = [];
                                    buckets[reason].push(c);
                                });

                                // Define display order and styling
                                const bucketConfig: Record<string, { label: string; color: string; dotColor: string }> = {
                                    "In Another Raid": { label: "In Another Raid", color: "text-cyan-700/70 dark:text-cyan-400/70", dotColor: "bg-cyan-400" },
                                    "Alt Signed": { label: "Alt Already Signed", color: "text-amber-700/70 dark:text-amber-400/70", dotColor: "bg-amber-400" },
                                    "Not Confirmed": { label: "Availability Unconfirmed", color: "text-orange-700/70 dark:text-orange-400/70", dotColor: "bg-orange-400" },
                                    "Unavailable": { label: "Time Conflict", color: "text-red-600/70 dark:text-red-400/70", dotColor: "bg-red-400" },
                                    "Locked Out": { label: "Raid Locked", color: "text-purple-600/70 dark:text-purple-400/70", dotColor: "bg-purple-400" },
                                    "Not Interested": { label: "Not Signed Up", color: "text-[var(--c-ink-4)]", dotColor: "bg-[var(--c-ink-4)]" },
                                };
                                const orderedReasons = ["In Another Raid", "Alt Signed", "Not Confirmed", "Unavailable", "Locked Out", "Not Interested"];

                                return orderedReasons.map(reason => {
                                    const chars = buckets[reason];
                                    if (!chars || chars.length === 0) return null;
                                    const config = bucketConfig[reason] || { label: reason, color: "text-[var(--c-ink-3)]", dotColor: "bg-[var(--c-ink-4)]" };

                                    const bucketOpen = openBuckets.has(reason);
                                    return (
                                        <div key={reason} className="space-y-2 pt-2 border-t border-[var(--c-line-soft)] first:border-t-0 first:pt-0">
                                            <button
                                                type="button"
                                                aria-expanded={bucketOpen}
                                                onClick={() => setOpenBuckets(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(reason)) next.delete(reason); else next.add(reason);
                                                    return next;
                                                })}
                                                className={`w-full text-[10px] font-bold uppercase tracking-wider ${config.color} flex items-center gap-2 rounded px-1 py-0.5 -mx-1 hover:bg-[var(--c-sunken)] transition-colors`}
                                            >
                                                <span className={`w-1.5 h-1.5 shrink-0 rounded-full ${config.dotColor}`}></span>
                                                <span className="truncate">{config.label}</span>
                                                <span className="shrink-0 rounded-full bg-[var(--c-sunken)] px-1.5 py-px text-[9px] text-[var(--c-ink-3)]">{chars.length}</span>
                                                <span className="ml-auto shrink-0 text-[var(--c-ink-4)]">{bucketOpen ? '▾' : '▸'}</span>
                                            </button>
                                            {bucketOpen && chars.map((c: any) => {
                                                const availableRoles = c.roles || c.metadata?.role_labels || ["DPS"];
                                                const charSquadInfo = c.userId ? squadLookup[c.userId] : null;
                                                const isSquadmateHovered = !!(hoveredSquadId && charSquadInfo?.squadId === hoveredSquadId);
                                                return (
                                                    <DraggableCard 
                                                        key={c.characterId} 
                                                        character={{ name: c.name, className: c.metadata?.class_name }}
                                                        wclScore={c.metadata?.wcl_perf?.score}
                                                        classIconUrl={getClassIconUrl(c.metadata?.class_name, currentGameFields)}
                                                        availableRoles={availableRoles}
                                                        specEntries={currentGameFields ? resolveSpecEntries(c, currentGameFields) : null}
                                                        unavailable={true}
                                                        unavailableReason={c.unavailableReason}
                                                        isInteractive={canManage}
                                                        squadInfo={charSquadInfo}
                                                        isSquadmateHovered={isSquadmateHovered}
                                                        onMouseEnter={() => { if (charSquadInfo) setHoveredSquadId(charSquadInfo.squadId); }}
                                                        onMouseLeave={() => setHoveredSquadId(null)}
                                                        onMouseDown={(e: any) => {
                                                            if (e.button !== 0) return;
                                                            setDragOrigin({ type: 'bench', id: c.characterId });
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    );
                                });
                            })()}

                            {/* Empty state */}
                            {ownBench.length === 0 && (
                                <div className="text-center text-[var(--c-ink-4)] text-xs py-4 italic">
                                    No players on bench
                                </div>
                            )}
                        </div>
                    </div>

                    {/* OTHER RAID TEAMS - released cross-guild players, available only */}
                    {crossGuildAvailable.length > 0 && (
                        <div className="bg-[var(--c-surface)] border border-purple-500/20 rounded-xl p-4 mt-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xs font-bold uppercase text-purple-700/80 dark:text-purple-300/80 flex items-center gap-2">
                                    <svg className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-3-6.7" />
                                    </svg>
                                    Available from Other Teams
                                </h3>
                                <span className="text-[10px] text-purple-700/50 dark:text-purple-300/50 font-bold">{crossGuildAvailable.length}</span>
                            </div>
                            <p className="text-[10px] text-[var(--c-ink-3)] mb-3 leading-snug">
                                Released players from other guilds, plus free agents (no guild) who confirmed availability
                                for this week. All are open to other raid teams and available for this raid.
                                Adding one sends them a Discord invite to accept or decline.
                            </p>
                            <div className="space-y-2">
                                {crossGuildAvailable.map((c: any) => {
                                    const availableRoles = c.roles || c.metadata?.role_labels || ["DPS"];
                                    const charSquadInfo = c.userId ? squadLookup[c.userId] : null;
                                    const isSquadmateHovered = !!(hoveredSquadId && charSquadInfo?.squadId === hoveredSquadId);
                                    return (
                                        <DraggableCard
                                            key={c.characterId}
                                            character={{ name: c.name, className: c.metadata?.class_name, isCrossGuild: true, isFreeAgent: c.isFreeAgent, sourceGuildName: c.sourceGuildName, sourceGuildTag: c.sourceGuildTag }}
                                            wclScore={c.metadata?.wcl_perf?.score}
                                            classIconUrl={getClassIconUrl(c.metadata?.class_name, currentGameFields)}
                                            availableRoles={availableRoles}
                                            specEntries={currentGameFields ? resolveSpecEntries(c, currentGameFields) : null}
                                            unavailable={false}
                                            isInteractive={canManage}
                                            squadInfo={charSquadInfo}
                                            isSquadmateHovered={isSquadmateHovered}
                                            onMouseEnter={() => {
                                              if (charSquadInfo) setHoveredSquadId(charSquadInfo.squadId);
                                              if (currentGameFields) {
                                                const tags = getCharacterProvidedTags(c, currentGameFields, null);
                                                setHoveredSpecTags(tags.length > 0 ? new Set(tags) : new Set());
                                              }
                                            }}
                                            onMouseLeave={() => { setHoveredSquadId(null); setHoveredSpecTags(new Set()); }}
                                            onMouseDown={(e: any) => {
                                                if (e.button !== 0) return;
                                                setDragOrigin({ type: 'bench', id: c.characterId });
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* SUGGESTIONS MODAL */}
            {suggestionsOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setSuggestionsOpen(false)} />
                    <div className="relative w-full max-w-2xl bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in-95">
                        <div className="p-5 border-b border-[var(--c-line)] flex justify-between items-center bg-[var(--c-bg-veil)]">
                            <h3 className="text-lg font-bold text-[var(--c-ink)]">Draft Candidates</h3>
                            <button onClick={() => setSuggestionsOpen(false)} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)]">×</button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-white/10">
                            {suggestions.length === 0 && (
                                <div className="text-center text-[var(--c-ink-3)] py-8">No candidates available for this raid.</div>
                            )}
                            {suggestions.map((group) => (
                                <div key={group.role} className="space-y-3">
                                    <div className="flex items-baseline justify-between border-b border-[var(--c-line)] pb-1">
                                        <div className="text-sm font-bold uppercase tracking-wider text-[var(--c-ink-2)]">{group.role}</div>
                                        <div className="text-xs text-[var(--c-ink-3)]">
                                            {group.candidates.length > 0
                                                ? `Top ${group.candidates.length}${group.needed > 0 ? ` · ${group.needed} needed` : ' · role filled'}`
                                                : 'No candidates available'}
                                        </div>
                                    </div>
                                    {group.candidates.length > 0 && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {group.candidates.map((c) => (
                                                <SuggestionCard
                                                    key={c.id}
                                                    candidate={c}
                                                    onClick={() => handleAddCandidate(c, group.role)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            
                        </div>
                        <div className="p-4 border-t border-[var(--c-line)] bg-[var(--c-bg-veil)] text-center">
                            <button onClick={() => setSuggestionsOpen(false)} className="px-6 py-2 bg-[var(--c-sunken-2)] hover:bg-[var(--c-sunken-2)] rounded text-sm font-bold text-[var(--c-ink)]">Done</button>
                        </div>
                    </div>
                </div>
            )}            

            {dragOrigin && dragPos && (
                <div className="fixed pointer-events-none z-[9999]" style={{ left: dragPos.x, top: dragPos.y }}>
                    <DraggableCard character={{ name: "Moving..." }} role="Moving" />
                </div>
            )}

            {/* REMOVAL BIN DOCK: appears only while dragging a rostered player.
                Drop on a reason zone = removed with that reason in one gesture.
                Drop anywhere else off-roster = removed as 'unspecified' + toast below. */}
            {canManage && dragOrigin && (dragOrigin.type === 'slot' || dragOrigin.type === 'benchseat') && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9998] pointer-events-none">
                    <div className="rounded-2xl border border-red-400/40 bg-[var(--c-surface)] px-4 py-3 shadow-2xl shadow-black/60">
                        <div className="mb-2 flex items-center gap-2">
                            <svg className="h-4 w-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            <span className="text-xs font-bold uppercase tracking-widest text-red-700 dark:text-red-300">Remove from raid</span>
                            <span className="text-[11px] text-[var(--c-ink-3)]">drop on a reason</span>
                        </div>
                        <div className="flex gap-2">
                            {REMOVAL_REASON_OPTIONS.map((r, i) => (
                                <div
                                    key={r.code}
                                    ref={el => { binChipRefs.current[i] = el; }}
                                    className={`rounded-full border px-4 py-2 text-xs font-bold transition-all ${
                                        binHoverIdx === i
                                            ? 'scale-110 border-red-400 bg-red-500/30 text-red-800 dark:text-red-100'
                                            : 'border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-2)]'
                                    }`}
                                >
                                    {r.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* REMOVAL REASON TOAST: non-blocking follow-up for untagged removals.
                Ignoring it is fine — the removal stays 'unspecified' (neutral). */}
            {removalPrompt && !dragOrigin && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9997]">
                    <div className="flex items-center gap-2 rounded-2xl border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-4 py-3 shadow-2xl shadow-black/60">
                        <span className="text-sm text-[var(--c-ink)]"><span className="font-bold text-amber-700 dark:text-amber-300">{removalPrompt.label}</span> removed.</span>
                        <span className="text-xs text-[var(--c-ink-3)]">Why?</span>
                        {REMOVAL_REASON_OPTIONS.filter(r => r.code !== 'other').map(r => (
                            <button
                                key={r.code}
                                onClick={() => setRemovalReason(removalPrompt.characterIds, r.code)}
                                className="rounded-full border border-[var(--c-line-strong)] bg-[var(--c-sunken)] px-3 py-1.5 text-xs font-bold text-[var(--c-ink-2)] transition-colors hover:border-amber-400/50 hover:bg-amber-400/10 hover:text-amber-800 dark:hover:text-amber-200"
                            >
                                {r.label}
                            </button>
                        ))}
                        <button
                            onClick={() => undoRemoval(removalPrompt.characterIds)}
                            className="ml-1 text-xs font-bold text-sky-700 dark:text-sky-300 hover:text-sky-800 dark:hover:text-sky-200"
                        >
                            Undo
                        </button>
                        <button
                            onClick={() => setRemovalPrompt(null)}
                            className="ml-1 text-[var(--c-ink-4)] hover:text-[var(--c-ink-2)]"
                            aria-label="Dismiss"
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}

{/* ROLE SELECTION MODAL */}
{roleSelection && (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setRoleSelection(null)} />
        <div className="relative w-full max-w-sm bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl p-6 animate-in zoom-in-95">
            <h3 className="text-lg font-bold text-[var(--c-ink)] mb-2">Assign Role</h3>
            <p className="text-sm text-[var(--c-ink-2)] mb-6">
                Select the role <strong>{roleSelection.candidate.name}</strong> will perform for this raid.
            </p>
            
            <div className="grid grid-cols-1 gap-2">
                {(roleSelection.candidate.roles || roleSelection.candidate.metadata?.role_labels || ["DPS"]).map((role: string) => (
                    <button
                        key={role}
                        onClick={() => confirmAssignment(role)}
                        className="flex items-center justify-between px-4 py-3 rounded bg-[var(--c-sunken)] hover:bg-[var(--c-sunken-2)] border border-[var(--c-line)] hover:border-[var(--c-line-strong)] transition-all group"
                    >
                        <span className="font-bold text-sm text-[var(--c-ink)]">{role}</span>
                        <span className="text-lg opacity-60 group-hover:opacity-100">{getRoleIcon(role)}</span>
                    </button>
                ))}
            </div>
            
            <button 
                onClick={() => setRoleSelection(null)}
                className="w-full mt-4 py-2 text-xs font-bold uppercase text-[var(--c-ink-4)] hover:text-[var(--c-ink)]"
            >
                Cancel
            </button>
        </div>
    </div>
)}

{/* SQUAD INTERCEPT MODAL */}
{squadIntercept && (() => {
    // Group ALL characters by userId (trigger + squadmates + their alts)
    const sourceRoster = candidates.length > 0 ? candidates : (guildRosterMap[activeRaid?.guild_id] || []);
    const userCharMap: Record<string, { userId: string; chars: { id: string; name: string; className: string; roles: string[] }[] }> = {};
    
    // Add trigger character first
    const triggerUserId = squadIntercept.triggerCharUserId;
    userCharMap[triggerUserId] = {
        userId: triggerUserId,
        chars: [{ id: squadIntercept.triggerCharId, name: squadIntercept.triggerCharName, className: squadIntercept.triggerCharClassName, roles: squadIntercept.triggerCharRoles }]
    };
    
    // Add trigger user's alts from bench
    const seatedCharIds = new Set(activeAssignments.map(a => a.character_id));
    sourceRoster.forEach((c: any) => {
        const uid = c.userId || c.ownerId;
        if (uid === triggerUserId && (c.characterId || c.id) !== squadIntercept.triggerCharId) {
            if (!seatedCharIds.has(c.characterId || c.id)) {
                userCharMap[triggerUserId].chars.push({
                    id: c.characterId || c.id,
                    name: c.name,
                    className: c.metadata?.class_name || c.className || "Unknown",
                    roles: c.roles || c.metadata?.role_labels || ["DPS"]
                });
            }
        }
    });
    
    // Add squadmates grouped by userId (they may have multiple chars/alts too)
    squadIntercept.availableSquadmates.forEach(sm => {
        if (sm.userId === triggerUserId) return; // Skip - already handled above
        if (!userCharMap[sm.userId]) {
            userCharMap[sm.userId] = { userId: sm.userId, chars: [] };
        }
        // Only add if not already in the list (avoid duplicates)
        if (!userCharMap[sm.userId].chars.some(c => c.id === sm.id)) {
            userCharMap[sm.userId].chars.push({ id: sm.id, name: sm.name, className: sm.className, roles: sm.roles });
        }
    });
    
    // Maintain order: trigger user first, then other squad members
    const allUsers = [userCharMap[triggerUserId], ...Object.values(userCharMap).filter(u => u.userId !== triggerUserId)];
    const totalSquadMembers = allUsers.length;
    const selectedCount = Object.keys(squadSelections).length;
    const allSelected = selectedCount === totalSquadMembers;
    
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => { setSquadIntercept(null); setSquadSelections({}); setAllowSquadSplit(false); }} />
            <div className="relative w-full max-w-md bg-[var(--c-surface)] border border-indigo-500/30 rounded-xl shadow-2xl p-6 animate-in zoom-in-95">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-[var(--c-ink)]">Seat Squad</h3>
                        <p className="text-sm text-indigo-700 dark:text-indigo-300">{squadIntercept.squadInfo.squadName}</p>
                    </div>
                    <button
                        onClick={() => {
                            if (!allowSquadSplit) {
                                setAllowSquadSplit(true);
                            } else {
                                const reselect: Record<string, { charId: string; roleLabel: string }> = {};
                                allUsers.forEach(u => {
                                    const firstChar = u.chars[0];
                                    if (firstChar) {
                                        reselect[u.userId] = { charId: firstChar.id, roleLabel: firstChar.roles[0] || "DPS" };
                                    }
                                });
                                setSquadSelections(reselect);
                                setAllowSquadSplit(false);
                            }
                        }}
                        className={`text-[10px] font-bold uppercase px-3 py-1.5 rounded-lg border transition-all ${
                            allowSquadSplit
                                ? 'bg-amber-500/20 border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/30'
                                : 'bg-[var(--c-sunken)] border-[var(--c-line-strong)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)]'
                        }`}
                    >
                        {allowSquadSplit ? "Re-link All" : "Split Squad"}
                    </button>
                </div>
                
                {/* Warning when split mode enabled */}
                {allowSquadSplit && (
                    <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-800 dark:text-amber-200">
                        <strong className="text-amber-700 dark:text-amber-300">Heads up:</strong> Squads signed up together because they want to raid together. Splitting them may cause friction or no-shows. Only exclude members if absolutely necessary for comp.
                    </div>
                )}
                
                {/* Squad member cards */}
                <div className="space-y-2 mb-4 max-h-[350px] overflow-y-auto">
                    {allUsers.map((user) => {
                        const sel = squadSelections[user.userId];
                        const isSelected = !!sel;
                        const selectedCharId = sel?.charId || user.chars[0]?.id;
                        const selectedChar = user.chars.find(c => c.id === selectedCharId) || user.chars[0];
                        const selectedRole = sel?.roleLabel || selectedChar?.roles[0] || "DPS";
                        const hasMultipleChars = user.chars.length > 1;
                        
                        return (
                            <div 
                                key={user.userId}
                                className={`p-3 rounded-lg border transition-all ${isSelected ? 'bg-indigo-500/10 border-indigo-500/40' : 'bg-[var(--c-sunken)] border-[var(--c-line)] opacity-50'}`}
                            >
                                <div className="flex items-center gap-3">
                                    {/* Checkbox - only functional in split mode */}
                                    <button
                                        onClick={() => {
                                            if (!allowSquadSplit) return; // Can't deselect unless split mode
                                            if (isSelected) {
                                                setSquadSelections(prev => { const n = {...prev}; delete n[user.userId]; return n; });
                                            } else {
                                                const firstChar = user.chars[0];
                                                setSquadSelections(prev => ({...prev, [user.userId]: { charId: firstChar.id, roleLabel: firstChar.roles[0] || "DPS" }}));
                                            }
                                        }}
                                        disabled={!allowSquadSplit}
                                        className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                                            isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-[var(--c-line-strong)]'
                                        } ${!allowSquadSplit ? 'cursor-default' : 'hover:border-[var(--c-ink-4)]'}`}
                                    >
                                        {isSelected && <svg className="w-4 h-4 text-[var(--c-ink)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                    </button>
                                    
                                    <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: getClassColor(selectedChar?.className || '') }} />
                                    
                                    {/* Character selector or static display */}
                                    <div className="flex-1 min-w-0">
                                        {hasMultipleChars && isSelected ? (
                                            <select
                                                value={selectedCharId}
                                                onChange={(e) => {
                                                    const newChar = user.chars.find(c => c.id === e.target.value);
                                                    setSquadSelections(prev => ({
                                                        ...prev,
                                                        [user.userId]: { charId: e.target.value, roleLabel: newChar?.roles[0] || "DPS" }
                                                    }));
                                                }}
                                                className="w-full bg-[var(--c-surface)] border border-[var(--c-line)] rounded px-2 py-1 text-sm font-bold text-[var(--c-ink)] focus:outline-none focus:border-indigo-500/50"
                                            >
                                                {user.chars.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name} ({c.className})</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <>
                                                <div className="font-bold text-[var(--c-ink)] text-sm truncate">{selectedChar?.name}</div>
                                                <div className="text-xs text-[var(--c-ink-3)]">{selectedChar?.className}</div>
                                            </>
                                        )}
                                    </div>
                                    
                                    {/* Spec pills */}
                                    {isSelected && selectedChar && (() => {
                                      const charData = sourceRoster.find((c: any) => c.characterId === selectedChar.id);
                                      const specEntries = charData && currentGameFields ? resolveSpecEntries(charData, currentGameFields) : [];
                                      if (specEntries.length > 1) {
                                        return (
                                          <div className="flex gap-1">
                                            {specEntries.map((entry: any) => (
                                              <button
                                                key={entry.roleLabel}
                                                onClick={() => setSquadSelections(prev => ({...prev, [user.userId]: { ...prev[user.userId], roleLabel: entry.roleLabel }}))}
                                                className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${selectedRole === entry.roleLabel ? 'bg-indigo-500 text-white' : 'bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)]'}`}
                                              >
                                                {entry.specLabel}
                                              </button>
                                            ))}
                                          </div>
                                        );
                                      } else if (specEntries.length === 1) {
                                        return <span className="px-2 py-1 rounded text-[10px] font-bold bg-indigo-500/20 text-indigo-700 dark:text-indigo-300">{specEntries[0].specLabel}</span>;
                                      } else if (selectedChar.roles.length === 1) {
                                        return <span className="px-2 py-1 rounded text-[10px] font-bold bg-indigo-500/20 text-indigo-700 dark:text-indigo-300">{selectedChar.roles[0]}</span>;
                                      }
                                      return null;
                                    })()}
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {/* Action button */}
                <button
                    onClick={handleSeatSquad}
                    disabled={selectedCount === 0 || (!allowSquadSplit && !allSelected)}
                    className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold transition-all"
                >
                    Seat {selectedCount} Player{selectedCount !== 1 ? 's' : ''}
                </button>
                
                {/* Validation message when trying to seat partial without split mode */}
                {!allowSquadSplit && !allSelected && selectedCount > 0 && (
                    <div className="mt-2 text-center text-xs text-amber-700 dark:text-amber-400">
                        Click the chain icon above to seat partial squad
                    </div>
                )}
                
                <button 
                    onClick={() => { setSquadIntercept(null); setSquadSelections({}); setAllowSquadSplit(false); }}
                    className="w-full mt-2 py-2 text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)] transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
})()}

{/* RATING MODAL */}
{showRatingModal && activeRaid && (
    // Fixed container starting below header (top-24). 
    // 'transform' creates a new containing block so the modal's 'fixed' positioning becomes relative to this div.
    <div className="fixed inset-0 top-24 z-[60]" style={{ transform: "translateZ(0)" }}>
        <CharacterRatingModal
            raidId={activeRaidId!}
            raidName={activeRaid.name || "Raid"}
            myCharacterId={
                activeAssignments.find(a => 
                    a.character_name === activeRaid.my_assignment?.name ||
                    a.character_user_id === me?.id
                )?.character_id || ""
            }
            onClose={() => setShowRatingModal(false)}
        />
    </div>
)}

{/* EDIT RAID MODAL */}
{showEditModal && editInfo && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setShowEditModal(false)} />
        <div className="relative w-full max-w-lg bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in-95">
            <div className="p-5 border-b border-[var(--c-line)] flex justify-between items-center bg-[var(--c-bg-veil)]">
                <h3 className="text-lg font-bold text-[var(--c-ink)]">Edit Raid Details</h3>
                <button onClick={() => setShowEditModal(false)} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] text-xl">&times;</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {editError && (
                    <div className="p-3 rounded bg-red-500/10 border border-red-500/30 text-red-800 dark:text-red-200 text-sm">
                        {editError}
                    </div>
                )}
                
                {/* Name */}
                <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-[var(--c-ink-2)]">Raid Name</label>
                    <input 
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full bg-[var(--c-surface)] border border-[var(--c-line)] rounded px-3 py-2 text-[var(--c-ink)] focus:border-indigo-500/50 outline-none"
                    />
                </div>

                {/* Raid Type */}
                <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-[var(--c-ink-2)]">Raid Instance</label>
                    <select
                        value={editRaidOptionId || ''}
                        onChange={(e) => { setEditRaidOptionId(Number(e.target.value) || null); setEditChainedRaidOptionIds([]); }}
                        className="w-full bg-[var(--c-surface)] border border-[var(--c-line)] rounded px-3 py-2 text-[var(--c-ink)] focus:border-indigo-500/50 outline-none"
                    >
                        <option value="">-- Select Raid --</option>
                        {(editInfo.raidOptions || []).map((opt: any) => (
                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                    </select>

                    {/* ── Raid Chaining (linked raids) ── */}
                    {editRaidOptionId && (() => {
                        const opts = editInfo.raidOptions || [];
                        const primaryOpt = opts.find((o: any) => o.id === editRaidOptionId);
                        const primarySize = primaryOpt?.metadata?.size;
                        // Eligible: same size, not the primary, not already chained.
                        const eligibleForChain = opts.filter((o: any) =>
                            o.id !== editRaidOptionId &&
                            !editChainedRaidOptionIds.includes(o.id) &&
                            o.metadata?.size === primarySize
                        );
                        const canAddMore = editChainedRaidOptionIds.length < 2 && eligibleForChain.length > 0;

                        return (
                            <div className="space-y-2 pt-1">
                                {/* Linked raids as pills */}
                                {editChainedRaidOptionIds.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {editChainedRaidOptionIds.map(optId => {
                                            const opt = opts.find((o: any) => o.id === optId);
                                            return (
                                                <div key={optId} className="flex items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-sm">
                                                    <svg className="w-3.5 h-3.5 text-cyan-700 dark:text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                    </svg>
                                                    <span className="text-cyan-800 dark:text-cyan-200 font-medium">{opt?.label || `Raid #${optId}`}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditChainedRaidOptionIds(prev => prev.filter(id => id !== optId))}
                                                        className="text-cyan-700/60 dark:text-cyan-400/60 hover:text-red-600 dark:hover:text-red-400 transition-colors ml-1"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Add-link dropdown */}
                                {canAddMore && (
                                    <div className="relative">
                                        <select
                                            value=""
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                if (val && !editChainedRaidOptionIds.includes(val)) {
                                                    setEditChainedRaidOptionIds(prev => [...prev, val]);
                                                }
                                            }}
                                            className="w-full appearance-none rounded-lg border border-dashed border-cyan-500/30 bg-cyan-500/5 px-3 py-2 text-sm text-cyan-700 dark:text-cyan-300 focus:border-cyan-500 focus:outline-none transition-colors hover:bg-cyan-500/10 cursor-pointer"
                                        >
                                            <option value="" className="bg-[var(--c-surface)] text-[var(--c-ink-2)]">+ Link Another Raid...</option>
                                            {eligibleForChain.map((opt: any) => (
                                                <option key={opt.id} value={opt.id} className="bg-[var(--c-surface)] text-[var(--c-ink)]">{opt.label} ({opt.metadata?.size}-man)</option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-cyan-700/50 dark:text-cyan-400/50">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                        </div>
                                    </div>
                                )}

                                {editChainedRaidOptionIds.length > 0 && (
                                    <div className="text-[10px] text-cyan-700/60 dark:text-cyan-300/60 pl-1">
                                        Linked raids share one roster, one session, and one soft reserve pool.
                                        Players locked to any raid in the chain are excluded.
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>

                {/* Sessions */}
                <div className="space-y-3">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-xs font-bold uppercase text-cyan-700/80 dark:text-cyan-400/80 tracking-widest">Schedule</span>
                      <PickerTzLabel
                        className="text-[10px] font-medium text-[var(--c-ink-3)]"
                        title="Date and times use this timezone. Saves are stored as UTC on the server."
                      />
                    </div>
                    {editSessions.map((session, idx) => (
                        <div key={idx} className="bg-[var(--c-sunken)] border border-[var(--c-line)] rounded-lg p-3 space-y-3">
                            {/* Date row */}
                            <div className="flex items-center gap-3">
                                <input 
                                    type="date"
                                    value={session.date}
                                    onChange={(e) => handleEditSessionChange(idx, 'date', e.target.value)}
                                    className="flex-1 bg-[var(--c-surface)] border border-[var(--c-line)] rounded px-2 py-1.5 text-[var(--c-ink)] text-sm focus:border-indigo-500/50 outline-none"
                                />
                            </div>
                            {/* Time row */}
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1 flex-1">
                                    <span className="text-xs text-[var(--c-ink-3)] w-8">Start</span>
                                    <button 
                                        type="button"
                                        onClick={() => bumpEditSessionStart(idx, -30)} 
                                        className="h-8 w-8 rounded bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center flex-shrink-0"
                                    >-</button>
                                    <div className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded px-3 py-1.5 text-[var(--c-ink)] text-sm min-w-[60px] text-center">
                                        {session.startTime}
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => bumpEditSessionStart(idx, 30)} 
                                        className="h-8 w-8 rounded bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center flex-shrink-0"
                                    >+</button>
                                </div>
                                <div className="flex items-center gap-1 flex-1">
                                    <span className="text-xs text-[var(--c-ink-3)] w-8">End</span>
                                    <button 
                                        type="button"
                                        onClick={() => bumpEditSessionEnd(idx, -30)} 
                                        className="h-8 w-8 rounded bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center flex-shrink-0"
                                    >-</button>
                                    <div className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded px-3 py-1.5 text-[var(--c-ink)] text-sm min-w-[60px] text-center">
                                        {session.endTime}
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => bumpEditSessionEnd(idx, 30)} 
                                        className="h-8 w-8 rounded bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center flex-shrink-0"
                                    >+</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Soft Reserves */}
                {editInfo.softReserveEnabled && (
                    <div className="space-y-3 pt-1">
                        <span className="text-xs font-bold uppercase text-cyan-700/80 dark:text-cyan-400/80 tracking-widest">Soft Reserves</span>
                        <div className="rounded-lg border border-red-500/20 bg-red-500/[0.04] p-4 space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-[var(--c-ink)]">Remove soft reserves from this raid</div>
                                    <div className="text-xs text-[var(--c-ink-3)] mt-1 leading-relaxed">
                                        Turns off the soft reserve system for this raid
                                        {editInfo.softReserveCount > 0
                                            ? ` and deletes all ${editInfo.softReserveCount} reserve${editInfo.softReserveCount === 1 ? '' : 's'} players have posted.`
                                            : '. No reserves have been posted yet.'}
                                        {' '}This cannot be undone.
                                    </div>
                                </div>
                            </div>
                            {!confirmRemoveSr ? (
                                <button
                                    type="button"
                                    onClick={() => setConfirmRemoveSr(true)}
                                    className="px-4 py-2 rounded text-sm font-bold text-red-800 dark:text-red-200 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30"
                                >
                                    Remove Soft Reserves
                                </button>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-red-800 dark:text-red-200 mr-1">Are you sure?</span>
                                    <button
                                        type="button"
                                        onClick={handleRemoveSoftReserve}
                                        disabled={srRemoving}
                                        className="px-4 py-2 rounded text-sm font-bold text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {srRemoving ? "Removing..." : "Yes, remove"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmRemoveSr(false)}
                                        disabled={srRemoving}
                                        className="px-4 py-2 rounded text-sm font-bold text-[var(--c-ink-2)] bg-[var(--c-sunken-2)] hover:bg-[var(--c-sunken-2)] disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="p-4 border-t border-[var(--c-line)] bg-[var(--c-bg-veil)] flex justify-end gap-3">
                <button 
                    onClick={() => setShowEditModal(false)} 
                    className="px-4 py-2 bg-[var(--c-sunken-2)] hover:bg-[var(--c-sunken-2)] rounded text-sm font-bold text-[var(--c-ink)]"
                >
                    Cancel
                </button>
                <button 
                    onClick={() => handleSaveEdit(false)} 
                    disabled={editSaving}
                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-sm font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {editSaving ? "Saving..." : "Save Changes"}
                </button>
            </div>
        </div>
    </div>
)}

{/* SCHEDULE CHANGE CONFIRMATION MODAL */}
{showScheduleConfirm && (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setShowScheduleConfirm(false)} />
        <div className="relative w-full max-w-md bg-[var(--c-surface)] border border-amber-500/30 rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95">
            {/* Header accent */}
            <div className="h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500" />
            
            <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-5 h-5 text-amber-700 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-amber-700 dark:text-amber-400">Schedule Change Detected</h3>
                        <p className="text-[var(--c-ink-2)] text-sm mt-1">You've modified the raid date or time.</p>
                    </div>
                </div>
                
                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 space-y-2">
                    <p className="text-[var(--c-ink)] text-sm leading-relaxed">
                        Changing the schedule may cause <strong className="text-amber-700 dark:text-amber-300">availability mismatches</strong> — players 
                        who signed up based on the original time may no longer be free, and their roster spots could become invalid.
                    </p>
                    {editInfo && editInfo.assignmentCount > 0 && (
                        <p className="text-amber-800/80 dark:text-amber-200/80 text-xs">
                            This raid currently has <strong>{editInfo.assignmentCount} player{editInfo.assignmentCount !== 1 ? 's' : ''}</strong> assigned to the roster.
                        </p>
                    )}
                </div>
                
                <div className="flex justify-end gap-3 pt-2">
                    <button 
                        onClick={() => setShowScheduleConfirm(false)}
                        className="px-4 py-2 bg-[var(--c-sunken-2)] hover:bg-[var(--c-sunken-2)] rounded text-sm font-bold text-[var(--c-ink)] transition-colors"
                    >
                        Go Back
                    </button>
                    <button 
                        onClick={() => { setShowScheduleConfirm(false); handleSaveEdit(true); }}
                        className="px-5 py-2 bg-amber-600 hover:bg-amber-500 rounded text-sm font-bold text-white transition-colors"
                    >
                        Save Anyway
                    </button>
                </div>
            </div>
        </div>
    </div>
)}

{/* CROSS-GUILD NOTIFY MODAL — composes the loot-rules DM and fires the send */}
{notifyModalOpen && activeRaidId && (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in"
            onClick={() => { if (!notifyLoading) setNotifyModalOpen(false); }}
        />
        <div className="relative w-full max-w-lg bg-[var(--c-surface)] border border-purple-500/30 rounded-xl shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in-95">
            <div className="p-5 border-b border-purple-500/20 flex justify-between items-center bg-purple-500/5">
                <div>
                    <h3 className="text-lg font-bold text-[var(--c-ink)]">Send Cross-Guild Invitation</h3>
                    <p className="text-[11px] text-purple-700/70 dark:text-purple-300/70 mt-0.5">
                        {notifyModalUserIds.length === 1 ? '1 player will receive a Discord DM' : `${notifyModalUserIds.length} players will receive a Discord DM`}
                    </p>
                </div>
                <button
                    onClick={() => { if (!notifyLoading) setNotifyModalOpen(false); }}
                    className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] text-xl disabled:opacity-50"
                    disabled={notifyLoading}
                >&times;</button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-[var(--c-ink-2)]">Loot System</label>
                    <p className="text-[10px] text-[var(--c-ink-3)] mb-2">
                        Pick the loot rules for this raid. The invited player will see this in their Discord DM before accepting.
                    </p>
                    {([
                        { value: 'soft_reserve',              label: 'Soft Reserve',                 desc: 'Players post their SRs on the raid page before invites go out.' },
                        { value: 'soft_reserve_hard_reserve', label: 'Soft Reserve + Hard Reserves', desc: 'Players SR, and some items are also reserved by the guild.' },
                        { value: 'dkp',                       label: 'DKP',                          desc: 'Items are awarded via DKP bidding.' },
                        { value: 'loot_council',              label: 'Loot Council',                 desc: 'Items are awarded at the discretion of the loot council.' },
                    ] as const).map(opt => {
                        const selected = notifyLootSystem === opt.value;
                        return (
                            <label
                                key={opt.value}
                                className={`flex items-start gap-3 px-3 py-2.5 rounded border cursor-pointer transition-colors ${selected ? 'border-purple-500/60 bg-purple-500/10' : 'border-[var(--c-line)] bg-[var(--c-surface)] hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken)]'}`}
                            >
                                <input
                                    type="radio"
                                    name="loot-system"
                                    value={opt.value}
                                    checked={selected}
                                    onChange={() => setNotifyLootSystem(opt.value)}
                                    className="mt-0.5 accent-purple-500"
                                />
                                <div className="flex flex-col min-w-0">
                                    <span className="text-sm font-bold text-[var(--c-ink)] leading-tight">{opt.label}</span>
                                    <span className="text-[11px] text-[var(--c-ink-3)] leading-snug mt-0.5">{opt.desc}</span>
                                </div>
                            </label>
                        );
                    })}
                </div>
            </div>

            <div className="p-4 border-t border-[var(--c-line)] bg-[var(--c-bg-veil)] flex items-center justify-end gap-3">
                <button
                    onClick={() => setNotifyModalOpen(false)}
                    disabled={notifyLoading}
                    className="px-4 py-2 bg-[var(--c-sunken-2)] hover:bg-[var(--c-sunken-2)] rounded text-sm font-bold text-[var(--c-ink)] transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    onClick={async () => {
                        if (!activeRaidId || notifyModalUserIds.length === 0) return;
                        setNotifyLoading(true);
                        try {
                            // Commit the board first. The notice rows the DM needs are created
                            // by the save endpoint, so an unsaved seat has nothing to notify,
                            // and a leader who hits Send should not also have to remember Save.
                            if (isDirty) {
                                const saved = await handleSaveRoster();
                                if (!saved) {
                                    // handleSaveRoster already reported why the save failed.
                                    showToast('No DMs sent.', 'error');
                                    return;
                                }
                            }
                            const res = await fetch(`/raids/${encodeURIComponent(activeRaidId)}/notify-cross-guild`, {
                                method: 'POST',
                                credentials: 'include',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    userIds: notifyModalUserIds,
                                    lootInfo: ({
                                        soft_reserve:              '🎲 Loot System: Soft Reserve\n• Post your SR on the raid page before invites go out.',
                                        soft_reserve_hard_reserve: '🎲 Loot System: Soft Reserve + Hard Reserves\n• Post your SR on the raid page before invites.\n• Some items are hard-reserved by the guild — the raid leader will confirm which.',
                                        dkp:                       '🎲 Loot System: DKP\n• Items are awarded via DKP bidding.',
                                        loot_council:              '🎲 Loot System: Loot Council\n• Items are awarded at the discretion of the loot council.',
                                    })[notifyLootSystem]
                                })
                            });
                            if (!res.ok) throw new Error('notify_failed');
                            const data = await res.json();
                            if (Array.isArray(data?.crossGuildNotices)) {
                                setCrossGuildNotices(data.crossGuildNotices);
                            }
                            const results: any[] = Array.isArray(data?.results) ? data.results : [];
                            const successCount = results.filter(r => r.success).length;
                            const failures     = results.filter(r => !r.success);
                            if (failures.length === 0) {
                                showToast(`Sent ${successCount} DM${successCount === 1 ? '' : 's'}.`, 'success');
                            } else if (successCount > 0) {
                                showToast(`Sent ${successCount}, ${failures.length} failed — check state badges.`, 'info');
                            } else {
                                showToast('No DMs sent — check state badges for details.', 'error');
                            }
                            setNotifyModalOpen(false);
                        } catch (err) {
                            showToast('Failed to send DMs.', 'error');
                        } finally {
                            setNotifyLoading(false);
                        }
                    }}
                    disabled={notifyLoading || notifyModalUserIds.length === 0}
                    className="px-5 py-2 bg-purple-600 hover:bg-purple-500 rounded text-sm font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {notifyLoading ? 'Sending…' : `Send DM${notifyModalUserIds.length === 1 ? '' : ` ×${notifyModalUserIds.length}`}`}
                </button>
            </div>
        </div>
    </div>
)}

        </div>        
    );
  }

  if (!me) {
    return themed(
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-6">
        <div className="max-w-md space-y-3">
          <Eyebrow>Raid planner</Eyebrow>
          <h2 className="text-3xl text-[var(--c-ink)]" style={DISPLAY}>Sign in to plan raids</h2>
          <p className="text-[15px] leading-relaxed text-[var(--c-ink-2)]">
            Connect your Discord to see the schedule, manage raid rosters and coordinate with your guild.
          </p>
        </div>
        <a
          href={`${API_URL}/auth/discord`}
          className={R.btnPrimary + " px-6 py-3 text-[15px]"}
        >
          Sign in with Discord
        </a>
      </div>
    );
  }

  const listMixesGames = new Set([...liveRaids, ...upcomingRaids, ...passedRaids].map((r) => r.gameVersion)).size > 1;

  return themed(
    <div className="max-w-7xl mx-auto space-y-10">
      {/* HERO: the Alignment sign up page's header, eyebrow + display title +
          amber hairline, with the guild emblem faded in behind it. */}
      <section className="relative isolate">
        {/* Guild emblem behind the heading: letterboxed, a little taller than the
            header, pinned to the left edge and faded out to the right. The blur is
            light on purpose so the emblem is still recognisable. */}
        {backdropLogoUrl && (
          <img
            src={backdropLogoUrl}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -left-4 top-1/2 -z-10 h-[150%] w-auto -translate-y-1/2 object-contain opacity-[0.18] blur-[3px] [mask-image:linear-gradient(to_right,#000_0%,#000_30%,transparent_80%)] [-webkit-mask-image:linear-gradient(to_right,#000_0%,#000_30%,transparent_80%)]"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        )}

        <div className="flex flex-col gap-6 pt-2 sm:pt-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Eyebrow>Raid planner</Eyebrow>
            <h1 className="mt-2 text-4xl leading-tight text-[var(--c-ink)] sm:text-5xl" style={DISPLAY}>
              Your raid week
            </h1>
            <div className="mt-4 h-px w-28 bg-gradient-to-r from-[var(--c-accent)] to-transparent" />
            <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-[var(--c-ink-2)]">
              Schedule raids, build rosters and see who is ready to go.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Admin "view as guild" mode is view/manage only: no raid creation, no ARC */}
            {!adminViewGuildId && (
              <>
                {guilds.length > 0 && (
                  <button onClick={() => setMode("arc")} className={R.btnSecondary + " inline-flex items-center gap-2 px-5 py-3"}>
                    <svg className="h-4 w-4 text-[var(--c-accent)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Auto schedule
                  </button>
                )}
                <button onClick={() => setMode("create")} className={R.btnPrimary + " inline-flex items-center gap-2 px-6 py-3 text-[15px]"}>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                  </svg>
                  Schedule raid
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* SCHEDULE LIST */}
      <section className="space-y-3">
        <SectionTitle count={liveRaids.length + upcomingRaids.length}>Upcoming raids</SectionTitle>

        {/* LIVE */}
        {liveRaids.length > 0 && (
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-2 px-1">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
              </span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-red-600 dark:text-red-400">Live now</span>
            </div>
            {liveRaids.map((r) => (
              <div key={r.id} className="ring-2 ring-red-500/30 rounded-2xl">
                <ScheduleCard
                  raid={r}
                  onClick={() => openRaidDetail(r.id)}
                  onLootCouncil={() => openLootCouncilFromRaid(r, "list")}
                  showGame={listMixesGames}
                />
              </div>
            ))}
          </div>
        )}

        {/* UPCOMING */}
        {upcomingRaids.length > 0 ? (
          upcomingRaids.map((r) => (
            <ScheduleCard
              key={r.id}
              raid={r}
              onClick={() => openRaidDetail(r.id)}
              onLootCouncil={() => openLootCouncilFromRaid(r, "list")}
                  showGame={listMixesGames}
            />
          ))
        ) : (
          <div className="rounded-3xl border border-dashed border-[var(--c-line-strong)] p-12 text-center">
            <div className="text-[15px] text-[var(--c-ink-3)]">{liveRaids.length > 0 ? 'No more upcoming raids.' : 'No upcoming raids scheduled.'}</div>
            {!adminViewGuildId && liveRaids.length === 0 && (
              <button onClick={() => setMode("create")} className={R.btnSecondary + " mt-5"}>Schedule one</button>
            )}
          </div>
        )}

        {/* PASSED */}
        {passedRaids.length > 0 && (
          <div className="pt-8 mt-8 border-t border-[var(--c-line-soft)]">
            <button 
              onClick={() => setShowPassed(!showPassed)}
              className="flex items-center gap-2 text-sm font-semibold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors outline-none"
            >
              <span>{showPassed ? 'Hide' : 'Show'} past raids ({passedRaids.length})</span>
              <svg 
                className={`w-3 h-3 transition-transform duration-300 ${showPassed ? 'rotate-180' : ''}`} 
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showPassed && (
              <div className="space-y-3 mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                {passedRaids.map((r) => (
                  <div key={r.id} className="opacity-50 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300">
                    <ScheduleCard
                      raid={r}
                      onClick={() => openRaidDetail(r.id)}
                      onLootCouncil={() => openLootCouncilFromRaid(r, "list")}
                  showGame={listMixesGames}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* RAID CHATS — previews most recent 3, expand for full log */}
      <PlannerChatSection />

      {/* Loot Queue Arena — discreet easter-egg launcher. Clicking = become host. */}
      <button
        type="button"
        onClick={() => setArenaHostSession(newArenaSessionId())}
        title="?"
        aria-label="Loot Queue Arena"
        className="fixed bottom-4 left-4 z-40 h-7 w-7 rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] opacity-25 text-[var(--c-ink-3)] hover:opacity-90 hover:text-amber-700 dark:hover:text-amber-300 hover:border-amber-400/40 transition-all flex items-center justify-center"
        style={{ backdropFilter: "blur(4px)" }}
      >
        <span className="text-[13px] leading-none">⚔️</span>
      </button>
      {arenaHostSession && (
        <LootQueueArena
          mode="host"
          sessionId={arenaHostSession}
          identity={arenaIdentity}
          pool={arenaPool}
          onClose={() => setArenaHostSession(null)}
        />
      )}
    </div>
  );
}